import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import AdminWallet from '../../components/admin/AdminWallet';
import AdminPayouts from '../../components/admin/AdminPayouts';
import AdminAgreements from '../../components/admin/AdminAgreements';
import BankDetailsForm from '../../components/BankDetailsForm';
import AdminTournaments from '../../components/admin/AdminTournaments';
import AdminFeeSettings from '../../components/admin/AdminFeeSettings';
import { API_URL as API } from '../../config/api';
import {
  IndianRupee, TrendingUp, Users, MapPin,
  Receipt, Percent, LogOut, RefreshCw,
  Download, Phone, Bell, X, CheckCheck,
  Building2, ShieldCheck, MessageSquare, BookOpen,
  ArrowLeft, Calendar, Clock, ToggleLeft, ToggleRight, Trash2, Menu,
  Eye, FileText, Check, Search, ExternalLink, CheckSquare, Square, UserCheck, UserX
} from 'lucide-react';
import { getTurfPriceRange, generateSlotsFromRange } from '../../components/partner/TimePicker12';

const getDocUrl = (img) => {
  if (!img) return null;
  if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) return img;
  const baseUrl = API.replace(/\/api\/?$/, '');
  return `${baseUrl}${img.startsWith('/') ? '' : '/'}${img}`;
};

// -- Pure SVG Pie Chart (no external library) ----------------------------------
function PieChart({ slices, size = 220 }) {
  const r = size / 2 - 10;
  const cx = size / 2;
  const cy = size / 2;
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (total === 0) return <div style={{ textAlign: 'center', color: '#374151', padding: '2rem' }}>No data</div>;

  let startAngle = -Math.PI / 2;
  const paths = slices.map((slice) => {
    const angle = (slice.value / total) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(startAngle + angle);
    const y2 = cy + r * Math.sin(startAngle + angle);
    const large = angle > Math.PI ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    const midAngle = startAngle + angle / 2;
    const lx = cx + (r * 0.65) * Math.cos(midAngle);
    const ly = cy + (r * 0.65) * Math.sin(midAngle);
    const pct = Math.round((slice.value / total) * 100);
    startAngle += angle;
    return { d, color: slice.color, label: slice.label, value: slice.value, lx, ly, pct };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
      <svg width={size} height={size} style={{ flexShrink: 0 }}>
        {paths.map((p, i) => (
          <path key={i} d={p.d} fill={p.color} stroke="white" strokeWidth="2">
            <title>{p.label}: ₹{p.value.toLocaleString()} ({p.pct}%)</title>
          </path>
        ))}
        {/* Donut hole */}
        <circle cx={cx} cy={cy} r={r * 0.45} fill="white" />
        <text x={cx} y={cy - 8} textAnchor="middle" fontSize="11" fontWeight="800" fill="#374151">TOTAL</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize="13" fontWeight="900" fill="#0f172a">₹{total.toLocaleString()}</text>
      </svg>
      {/* Legend */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {paths.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: p.color, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151' }}>{p.label}</div>
              <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '600' }}>₹{p.value.toLocaleString()} ({p.pct}%)</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminCalendar({ selectedDate, onChangeDate, colors }) {
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const numDays = new Date(year, month + 1, 0).getDate();
  const firstDayIndex = new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  const monthName = currentMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  const days = [];
  for (let i = 0; i < firstDayIndex; i++) {
    days.push(null);
  }
  for (let i = 1; i <= numDays; i++) {
    days.push(new Date(year, month, i));
  }

  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <div style={{
      width: '100%',
      maxWidth: '360px',
      background: 'white',
      borderRadius: '16px',
      border: '1.5px solid #EEF2E6',
      padding: '1rem',
      fontFamily: "'Inter', sans-serif",
      fontSize: '0.85rem'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <button 
          onClick={handlePrevMonth} 
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '800', color: '#084734', padding: '4px 8px' }}
        >
          &lt;
        </button>
        <div style={{ fontWeight: '800', color: '#0f172a' }}>{monthName}</div>
        <button 
          onClick={handleNextMonth} 
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '800', color: '#084734', padding: '4px 8px' }}
        >
          &gt;
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center', fontWeight: '700', color: '#98A2B3', marginBottom: '6px', fontSize: '0.75rem' }}>
        {weekdays.map(day => (
          <div key={day}>{day}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
        {days.map((date, idx) => {
          if (!date) return <div key={`empty-${idx}`} />;
          const isSelected = date.toDateString() === selectedDate.toDateString();
          const isToday = date.toDateString() === new Date().toDateString();
          return (
            <button
              key={idx}
              onClick={() => onChangeDate(date)}
              style={{
                width: '100%',
                aspectRatio: '1',
                borderRadius: '8px',
                border: 'none',
                background: isSelected ? '#084734' : isToday ? '#DCEFB8' : 'transparent',
                color: isSelected ? 'white' : '#0f172a',
                fontWeight: isSelected || isToday ? '800' : '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s',
                outline: 'none',
                fontSize: '0.75rem'
              }}
              onMouseEnter={e => {
                if (!isSelected) e.currentTarget.style.background = '#F8FAF7';
              }}
              onMouseLeave={e => {
                if (!isSelected) e.currentTarget.style.background = isToday ? '#DCEFB8' : 'transparent';
              }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { token, user, logout, isLoading } = useAuth();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isDesktop = windowWidth > 1024;
  const [allVenues, setAllVenues] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [kycList, setKycList] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [sideLoading, setSideLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all'); // all | user | owner | admin
  const [dateFilter, setDateFilter] = useState('all'); // all | today | month | year
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [allOffers, setAllOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const now = new Date();
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(() => new Date());
  const showAdminToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const [previewKycImage, setPreviewKycImage] = useState(null);
  const [kycSearch, setKycSearch] = useState('');
  const [kycStatusFilter, setKycStatusFilter] = useState('all');
  const [reviewKycModal, setReviewKycModal] = useState(false);
  const [reviewKycTarget, setReviewKycTarget] = useState(null);
  const [reviewKycStatus, setReviewKycStatus] = useState('approved');
  const [reviewKycNote, setReviewKycNote] = useState('');
  const [reviewKycSaving, setReviewKycSaving] = useState(false);

  const handleReviewKYC = async (kycId, status, note = '') => {
    setReviewKycSaving(true);
    try {
      await axios.put(`${API}/kyc/admin/review/${kycId}`, {
        status,
        admin_note: note
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showAdminToast(`KYC request ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchKYC();
      setReviewKycModal(false);
    } catch (err) {
      console.error(err);
      showAdminToast(err.response?.data?.msg || 'Failed to update KYC status', 'error');
    } finally {
      setReviewKycSaving(false);
    }
  };

  const [selectedUserWallet, setSelectedUserWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [manualCreditAmount, setManualCreditAmount] = useState('');
  const [manualCreditDesc, setManualCreditDesc] = useState('');
  const [manualCreditLoading, setManualCreditLoading] = useState(false);

  const fetchUserWallet = async (userId) => {
    setWalletLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/wallet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUserWallet(res.data);
    } catch (err) {
      console.error(err);
      showAdminToast('Failed to load wallet details', 'error');
    } finally {
      setWalletLoading(false);
    }
  };

  const handleManualCredit = async () => {
    const amt = parseFloat(manualCreditAmount);
    if (!amt || amt <= 0) {
      showAdminToast('Please enter a valid amount greater than 0', 'error');
      return;
    }
    if (!manualCreditDesc.trim()) {
      showAdminToast('Please enter a reason/description', 'error');
      return;
    }
    setManualCreditLoading(true);
    try {
      await axios.post(`${API}/admin/users/${selectedUser._id}/wallet/credit`, {
        amount: amt,
        description: manualCreditDesc.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showAdminToast(`Manually credited ₹${amt} to partner wallet!`);
      setManualCreditAmount('');
      setManualCreditDesc('');
      fetchUserWallet(selectedUser._id);
    } catch (err) {
      console.error(err);
      showAdminToast(err.response?.data?.detail || 'Failed to credit wallet', 'error');
    } finally {
      setManualCreditLoading(false);
    }
  };

  useEffect(() => {
    if (selectedUser && selectedUser.role === 'owner') {
      fetchUserWallet(selectedUser._id);
    } else {
      setSelectedUserWallet(null);
    }
  }, [selectedUser]);

  // ── Notifications ──────────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [readIds, setReadIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('admin_read_notifs') || '[]'); } catch { return []; }
  });
  const notifRef = useRef(null);

  // Build notifications from fetched data
  const buildNotifications = (venues, tickets, kyc, bookings) => {
    const notifs = [];

    // Pending venue approvals
    (venues || []).filter(v => v.status === 'pending').forEach(v => {
      notifs.push({
        id: `venue_${v._id}`,
        type: 'venue',
        icon: <Building2 size={16} />,
        color: '#F59E0B',
        bg: '#FEF3C7',
        title: 'New Venue Pending Approval',
        message: `"${v.name}" submitted by ${v.ownerName || 'a partner'}`,
        time: v.createdAt,
        action: () => setTab('venues'),
      });
    });

    // Open support tickets
    (tickets || []).filter(t => t.status === 'Open').slice(0, 5).forEach(t => {
      notifs.push({
        id: `ticket_${t.id || t._id}`,
        type: 'support',
        icon: <MessageSquare size={16} />,
        color: '#3B82F6',
        bg: '#EFF6FF',
        title: 'Open Support Ticket',
        message: `"${t.subject}" from ${t.user_id?.name || 'a user'}`,
        time: t.createdAt,
        action: () => setTab('support'),
      });
    });

    // Pending KYC
    (kyc || []).filter(k => k.status === 'pending').slice(0, 5).forEach(k => {
      notifs.push({
        id: `kyc_${k.id || k._id}`,
        type: 'kyc',
        icon: <ShieldCheck size={16} />,
        color: '#8B5CF6',
        bg: '#F5F3FF',
        title: 'KYC Verification Pending',
        message: `${k.business_name || 'A partner'} submitted KYC documents`,
        time: k.created_at || k.createdAt,
        action: () => setTab('kyc'),
      });
    });

    // Recent bookings (last 5)
    (bookings || []).slice(0, 5).forEach(b => {
      notifs.push({
        id: `booking_${b.id || b._id}`,
        type: 'booking',
        icon: <BookOpen size={16} />,
        color: '#10B981',
        bg: '#ECFDF5',
        title: 'New Booking',
        message: `${b.user_name || 'A user'} booked ${b.turf_name || 'a venue'}`,
        time: b.createdAt,
        action: () => setTab('transactions'),
      });
    });

    // Sort by time descending
    notifs.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
    setNotifications(notifs);
  };

  const unreadCount = notifications.filter(n => !readIds.includes(n.id)).length;

  const markAllRead = () => {
    const allIds = notifications.map(n => n.id);
    setReadIds(allIds);
    localStorage.setItem('admin_read_notifs', JSON.stringify(allIds));
  };

  const markOneRead = (id) => {
    const updated = [...new Set([...readIds, id])];
    setReadIds(updated);
    localStorage.setItem('admin_read_notifs', JSON.stringify(updated));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifs(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (isLoading) return;
    // If not logged in at all ? go to admin login
    if (!token || !user) {
      navigate('/admin/login');
      return;
    }
    // If logged in but not admin/owner ? go to admin login
    if (user.role !== 'admin' && user.role !== 'owner') {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [token, user, isLoading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/revenue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      // Pre-fetch notification data in background
      fetchNotifications(res.data?.allBookings || []);
    } catch (err) {
      console.error(err);
      alert('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const [pendingTournamentsCount, setPendingTournamentsCount] = useState(0);

  const fetchNotifications = async (bookings = []) => {
    try {
      const [venuesRes, ticketsRes, kycRes, tournRes] = await Promise.allSettled([
        axios.get(`${API}/admin/turfs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/support/tickets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/kyc/admin/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tournaments/owner`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const venues  = venuesRes.status  === 'fulfilled' ? venuesRes.value.data  : [];
      const tickets = ticketsRes.status === 'fulfilled' ? ticketsRes.value.data : [];
      const kyc     = kycRes.status     === 'fulfilled' ? kycRes.value.data     : [];
      const tourns  = tournRes.status   === 'fulfilled' ? tournRes.value.data   : [];
      setAllVenues(venues);
      setSupportTickets(tickets);
      setKycList(kyc);
      setPendingTournamentsCount((tourns || []).filter(t => !t.is_approved).length);
      buildNotifications(venues, tickets, kyc, bookings);
    } catch (err) { console.error(err); }
  };

  const fetchVenues = async () => {
    setSideLoading(true);
    try {
      const res = await axios.get(`${API}/admin/turfs`, { headers: { Authorization: `Bearer ${token}` } });
      setAllVenues(res.data);
    } catch (err) { console.error(err); }
    finally { setSideLoading(false); }
  };

  const fetchKYC = async () => {
    setSideLoading(true);
    try {
      const res = await axios.get(`${API}/kyc/admin/all`, { headers: { Authorization: `Bearer ${token}` } });
      setKycList(res.data);
    } catch (err) { console.error(err); }
    finally { setSideLoading(false); }
  };

  const fetchSupport = async () => {
    setSideLoading(true);
    try {
      const res = await axios.get(`${API}/admin/support/tickets`, { headers: { Authorization: `Bearer ${token}` } });
      setSupportTickets(res.data);
    } catch (err) { console.error(err); }
    finally { setSideLoading(false); }
  };

  const fetchUsers = async () => {
    setSideLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setAllUsers(res.data);
    } catch (err) { console.error(err); }
    finally { setSideLoading(false); }
  };

  const handleToggleSelectUser = (id, e) => {
    if (e) e.stopPropagation();
    setSelectedUserIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllUsers = (visibleUsers) => {
    const selectable = visibleUsers.filter(u => u.role !== 'admin').map(u => u._id);
    const allSelected = selectable.length > 0 && selectable.every(id => selectedUserIds.includes(id));
    if (allSelected) {
      setSelectedUserIds(prev => prev.filter(id => !selectable.includes(id)));
    } else {
      setSelectedUserIds(prev => Array.from(new Set([...prev, ...selectable])));
    }
  };

  const handleBulkStatus = async (isActive) => {
    if (selectedUserIds.length === 0) return;
    setBulkActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/users/bulk-status`, {
        user_ids: selectedUserIds,
        is_active: isActive
      }, { headers: { Authorization: `Bearer ${token}` } });
      showAdminToast(res.data.msg || `Updated status for ${res.data.count} users`);
      setSelectedUserIds([]);
      fetchUsers();
    } catch (err) {
      console.error(err);
      showAdminToast(err.response?.data?.detail || err.response?.data?.msg || 'Failed to update users status', 'error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to permanently delete the ${selectedUserIds.length} selected user(s)?\n\nAll associated venues and partner data will be permanently removed. This action cannot be undone.`)) return;
    
    setBulkActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/users/bulk-delete`, {
        user_ids: selectedUserIds
      }, { headers: { Authorization: `Bearer ${token}` } });
      showAdminToast(res.data.msg || `Deleted ${res.data.count} users successfully`);
      setSelectedUserIds([]);
      fetchUsers();
    } catch (err) {
      console.error(err);
      showAdminToast(err.response?.data?.detail || err.response?.data?.msg || 'Failed to delete users', 'error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const fetchOffers = async () => {
    setSideLoading(true);
    try {
      const res = await axios.get(`${API}/admin/offers`, { headers: { Authorization: `Bearer ${token}` } });
      setAllOffers(res.data);
    } catch (err) { console.error(err); }
    finally { setSideLoading(false); }
  };

  useEffect(() => {
    setSelectedUser(null);
    setSelectedUserIds([]);
    setRoleFilter('all');
    setSelectedBooking(null);
    setSelectedVenue(null);
    setSelectedOffer(null);
    if (tab === 'venues' && allVenues.length === 0) fetchVenues();
    if (tab === 'users' && allUsers.length === 0) fetchUsers();
    if (tab === 'kyc') fetchKYC();
    if (tab === 'support' && supportTickets.length === 0) fetchSupport();
    if (tab === 'offers' && allOffers.length === 0) fetchOffers();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportCSV = () => {
    if (!filteredTransactions) return;
    const rows = [
      ['Booking Ref', 'Customer', 'Phone', 'Turf', 'Date', 'Time Slot',
       'Turf Price', 'User Platform Fee', 'User GST',
       'Partner Platform Fee', 'Partner GST', 'Partner Payout',
       'Admin Earnings', 'Total Paid by User'],
      ...filteredTransactions.map(t => [
        t.bookingRef, t.userName, t.userPhone, t.turfName,
        t.date, t.timeSlot,
        t.baseAmount,
        t.userPlatformFee,
        t.userGst,
        t.partnerPlatformFee,
        t.partnerGst,
        t.partnerPayout,
        t.adminEarnings,
        t.totalPaid,
      ])
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `turfx-revenue-${dateFilter}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (isLoading || loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: colors.background }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '48px', height: '48px', border: `4px solid ${colors.border}`, borderTop: '4px solid #CEF17B', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
        <p style={{ color: colors.textSecondary, fontWeight: '700' }}>Loading admin data...</p>
      </div>
    </div>
  );

  const getFilteredBookings = () => {
    if (!data?.allBookings) return [];
    if (dateFilter === 'all') return data.allBookings;
    const now = new Date();
    
    return data.allBookings.filter(b => {
      const rawDate = b.createdAt || b.created_at || b.date;
      if (!rawDate) return false;
      const bDate = new Date(rawDate);
      if (isNaN(bDate.getTime())) return false;
      
      if (dateFilter === 'today') {
        return bDate.toDateString() === now.toDateString();
      } else if (dateFilter === 'week') {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const dayOfWeek = today.getDay(); // 0 is Sunday, 1 is Monday, ...
        const distanceToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - distanceToMonday);
        startOfWeek.setHours(0,0,0,0);
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 7);
        
        return bDate >= startOfWeek && bDate < endOfWeek;
      } else if (dateFilter === 'month') {
        return bDate.getMonth() === now.getMonth() && bDate.getFullYear() === now.getFullYear();
      } else if (dateFilter === 'year') {
        return bDate.getFullYear() === now.getFullYear();
      }
      return true;
    });
  };

  const getDailyComposition = (targetDate) => {
    const dailyStats = {
      userPlatformFee: 0,
      userGST: 0,
      partnerPlatformFee: 0,
      partnerGST: 0,
      turfAdminEarnings: 0,
      tournamentCustomerFee: 0,
      tournamentPartnerFee: 0,
      tournamentAdminEarnings: 0,
      adminEarnings: 0
    };
    if (!data?.allBookings) return dailyStats;
    
    const targetYear = targetDate.getFullYear();
    const targetMonth = targetDate.getMonth();
    const targetDay = targetDate.getDate();
    
    data.allBookings.forEach(b => {
      const rawDate = b.createdAt || b.created_at || b.date;
      if (!rawDate) return;
      const status = (b.status || '').toLowerCase();
      if (!['confirmed', 'completed', 'checked-in', 'approved'].includes(status)) return;
      
      const bDate = new Date(rawDate);
      if (isNaN(bDate.getTime())) return;
      
      const matchesDate = (
        (bDate.getFullYear() === targetYear && bDate.getMonth() === targetMonth && bDate.getDate() === targetDay) ||
        bDate.toDateString() === targetDate.toDateString()
      );

      if (matchesDate) {
        const fees = calcBookingFees(b);
        if (b.is_tournament || b.type === 'tournament') {
          dailyStats.tournamentCustomerFee += fees.bUFee;
          dailyStats.tournamentPartnerFee += fees.bPFee;
          dailyStats.tournamentAdminEarnings += fees.bAdminEarn;
        } else {
          dailyStats.userPlatformFee += fees.bUFee;
          dailyStats.userGST += fees.bUGst;
          dailyStats.partnerPlatformFee += fees.bPFee;
          dailyStats.partnerGST += fees.bPGst;
          dailyStats.turfAdminEarnings += fees.bAdminEarn;
        }
        dailyStats.adminEarnings += fees.bAdminEarn;
      }
    });
    
    // Format to 2 decimal places
    Object.keys(dailyStats).forEach(k => {
      dailyStats[k] = +dailyStats[k].toFixed(2);
    });
    
    return dailyStats;
  };

  const getUserCity = (u) => {
    if (u.role === 'owner') {
      const venue = allVenues.find(v => v.owner_id?._id === u._id || v.owner_id?.id === u._id || v.owner_id === u._id);
      if (venue?.city) return venue.city;
    }
    if (data?.allBookings) {
      const booking = data.allBookings.find(b => b.user_id?._id === u._id || b.user_id?.id === u._id || b.user_id === u._id);
      if (booking?.turf_city) return booking.turf_city;
    }
    return '—';
  };

  const filteredBookings = getFilteredBookings();

  const calcBookingFees = (b) => {
    let bTot = Number(b.total_price || b.totalPaid || b.amount_paid || b.amount || b.entry_fee || 0);
    let bBase = Number(b.base_amount || b.court_amount || b.baseAmount || b.entry_fee || 0);
    let bUFee = Number(b.user_platform_fee || b.platform_fee || b.userPlatformFee || b._u_fee || 0);
    let bUGst = Number(b.user_gst || b.gst || b.userGst || b._u_gst || 0);
    let bPFee = Number(b.partner_platform_fee || b.turf_fee || b.partnerPlatformFee || b._p_fee || 0);
    let bPGst = Number(b.partner_gst || b.partnerGst || b._p_gst || 0);
    let bAdminEarn = Number(b.admin_earnings || b.adminEarnings || b._admin_earn || 0);
    let bPPayout = Number(b.partner_payout || b.partnerPayout || b._p_payout || 0);

    if (bBase <= 0 && bTot > 0) {
      bBase = +(bTot / 1.059).toFixed(2);
    } else if (bTot <= 0 && bBase > 0) {
      const uFeeEst = +(bBase * 0.05).toFixed(2);
      const uGstEst = +(uFeeEst * 0.18).toFixed(2);
      bTot = +(bBase + uFeeEst + uGstEst).toFixed(2);
    }

    if (bBase > 0) {
      if (bUFee <= 0) bUFee = +(bBase * 0.05).toFixed(2);
      if (bUGst <= 0 && bUFee > 0) bUGst = +(bUFee * 0.18).toFixed(2);
      if (bPFee <= 0) bPFee = +(bBase * 0.05).toFixed(2);
      if (bPGst <= 0 && bPFee > 0) bPGst = +(bPFee * 0.18).toFixed(2);
    } else {
      if (bUGst <= 0 && bUFee > 0) bUGst = +(bUFee * 0.18).toFixed(2);
      if (bPGst <= 0 && bPFee > 0) bPGst = +(bPFee * 0.18).toFixed(2);
    }

    // Admin revenue includes fees and taxes paid by both customer (User GST) and vendor (Partner GST)
    bAdminEarn = +(bUFee + bUGst + bPFee + bPGst).toFixed(2);

    // Partner Payout = Amount sent to partner deducting platform fees & taxes of both customer and partner
    bPPayout = Math.max(0, +(bTot - bAdminEarn).toFixed(2));

    return { bTot, bBase, bUFee, bUGst, bPFee, bPGst, bAdminEarn, bPPayout };
  };

  const computedStats = (() => {
    const summary = data?.summary || {};
    const defaultStats = {
      totalBookings: summary.totalBookings || (data?.allBookings?.length || 0),
      totalGMV: summary.totalGMV || 0,
      totalBaseAmount: summary.totalBaseAmount || 0,
      totalUserPlatformFee: summary.totalUserPlatformFee || summary.totalPlatformFee || 0,
      totalUserGST: summary.totalUserGST || 0,
      totalPartnerPlatformFee: summary.totalPartnerPlatformFee || 0,
      totalPartnerGST: summary.totalPartnerGST || 0,
      turfUserFee: summary.turfUserFee || 0,
      turfPartnerFee: summary.turfPartnerFee || 0,
      turfAdminEarnings: summary.turfAdminEarnings || 0,
      tournamentCustomerFee: summary.tournamentCustomerFee || 0,
      tournamentPartnerFee: summary.tournamentPartnerFee || 0,
      tournamentAdminEarnings: summary.tournamentAdminEarnings || 0,
      totalAdminEarnings: summary.totalAdminEarnings || summary.totalRevenue || 0,
      totalPartnerPayout: summary.totalPartnerPayout || 0,
      totalUsers: summary.totalUsers || 0,
      totalPartners: summary.totalPartners || 0,
      totalTurfs: summary.totalTurfs || 0,
      userPlatformFeePercent: summary.userPlatformFeePercent || 5,
      partnerPlatformFeePercent: summary.partnerPlatformFeePercent || 5,
      gstRate: summary.gstRate || 18,
      totalGST: summary.totalGST !== undefined ? summary.totalGST : ((summary.totalUserGST || 0) + (summary.totalPartnerGST || 0)),
      payuTotalTransactions: summary.payuTotalTransactions !== undefined ? summary.payuTotalTransactions : (summary.totalBookings || 0),
      payuTotalGMV: summary.payuTotalGMV !== undefined ? summary.payuTotalGMV : (summary.totalGMV || 0),
      payuAdminEarnings: summary.payuAdminEarnings !== undefined ? summary.payuAdminEarnings : (summary.totalAdminEarnings || summary.totalRevenue || 0),
      payuPartnerPayout: summary.payuPartnerPayout !== undefined ? summary.payuPartnerPayout : (summary.totalPartnerPayout || 0),
    };

    const bookingsSource = dateFilter === 'all' ? (data?.allBookings || []) : filteredBookings;
    
    if (bookingsSource.length > 0) {
      let bookingsCount = 0;
      let gmv = 0, base = 0, uFee = 0, uGst = 0, pFee = 0, pGst = 0, adminEarn = 0, pPayout = 0;
      let turfUFee = 0, turfPFee = 0, turfAdmin = 0;
      let tournCFee = 0, tournPFee = 0, tournAdmin = 0;

      bookingsSource.forEach(b => {
        const status = (b.status || '').toLowerCase();
        if (!['confirmed', 'completed', 'checked-in', 'approved'].includes(status)) return;
        const fees = calcBookingFees(b);
        bookingsCount += 1;
        gmv += fees.bTot;
        base += fees.bBase;
        uFee += fees.bUFee;
        uGst += fees.bUGst;
        pFee += fees.bPFee;
        pGst += fees.bPGst;
        adminEarn += fees.bAdminEarn;
        pPayout += fees.bPPayout;

        if (b.is_tournament || b.type === 'tournament') {
          tournCFee += fees.bUFee;
          tournPFee += fees.bPFee;
          tournAdmin += fees.bAdminEarn;
        } else {
          turfUFee += fees.bUFee;
          turfPFee += fees.bPFee;
          turfAdmin += fees.bAdminEarn;
        }
      });

      if (bookingsCount > 0) {
        const calculatedGMV = +gmv.toFixed(2);
        const calculatedAdminEarn = +adminEarn.toFixed(2);
        const calculatedPayout = +pPayout.toFixed(2);
        const calculatedGST = +(uGst + pGst).toFixed(2);

        return {
          ...defaultStats,
          totalBookings: bookingsCount,
          totalGMV: calculatedGMV,
          totalBaseAmount: +base.toFixed(2),
          totalUserPlatformFee: +uFee.toFixed(2),
          totalUserGST: +uGst.toFixed(2),
          totalPartnerPlatformFee: +pFee.toFixed(2),
          totalPartnerGST: +pGst.toFixed(2),
          turfUserFee: +turfUFee.toFixed(2),
          turfPartnerFee: +turfPFee.toFixed(2),
          turfAdminEarnings: +turfAdmin.toFixed(2),
          tournamentCustomerFee: +tournCFee.toFixed(2),
          tournamentPartnerFee: +tournPFee.toFixed(2),
          tournamentAdminEarnings: +tournAdmin.toFixed(2),
          totalAdminEarnings: calculatedAdminEarn,
          totalPartnerPayout: calculatedPayout,
          totalGST: calculatedGST,
          payuTotalTransactions: bookingsCount,
          payuTotalGMV: calculatedGMV,
          payuAdminEarnings: calculatedAdminEarn,
          payuPartnerPayout: calculatedPayout,
        };
      }
    }
    
    return defaultStats;
  })();

  const s = computedStats;

  const getFilteredMonthly = () => {
    const monthlyMap = {};
    const sourceBookings = filteredBookings.length > 0 ? filteredBookings : (data?.allBookings || []);
    sourceBookings.forEach(b => {
      const rawDate = b.createdAt || b.created_at || b.date;
      if (!rawDate) return;
      const status = (b.status || '').toLowerCase();
      if (!['confirmed', 'completed', 'checked-in', 'approved'].includes(status)) return;
      const month = String(rawDate).substring(0, 7); // "YYYY-MM"
      if (!monthlyMap[month]) {
        monthlyMap[month] = { month, revenue: 0, adminEarnings: 0 };
      }
      monthlyMap[month].revenue += b.admin_earnings || (b.user_platform_fee || 0) + (b.partner_platform_fee || 0);
      monthlyMap[month].adminEarnings += b.admin_earnings || (b.user_platform_fee || 0) + (b.partner_platform_fee || 0);
    });
    return Object.values(monthlyMap).sort((a, b) => a.month.localeCompare(b.month));
  };
  const monthly = getFilteredMonthly();
  const perTurf = data?.perTurf || [];

  const sourceTransactions = filteredBookings.length > 0 ? filteredBookings : (data?.allBookings || data?.recentTransactions || []);
  const filteredTransactions = sourceTransactions.map(b => {
    const fees = calcBookingFees(b);
    return {
      bookingRef:         (b.bookingRef || b.booking_id || b._id?.slice(-6) || b.id?.slice(-6) || '').toUpperCase(),
      userName:           b.userName || b.user_name || b.user_id?.name || 'Customer',
      userPhone:          b.userPhone || b.user_phone || b.user_id?.phone || '-',
      turfName:           b.turfName || b.turf_name || b.turf_id?.name || 'Venue',
      date:               b.date || (b.createdAt ? b.createdAt.split('T')[0] : '-'),
      timeSlot:           b.timeSlot || b.time_slot || (b.time_slots || [])[0] || '-',
      baseAmount:         fees.bBase,
      userPlatformFee:    fees.bUFee,
      userGst:            fees.bUGst,
      partnerPlatformFee: fees.bPFee,
      partnerGst:         fees.bPGst,
      partnerPayout:      fees.bPPayout,
      adminEarnings:      fees.bAdminEarn,
      totalPaid:          fees.bTot,
      status:             b.status || 'confirmed',
    };
  });

  const transactions = filteredTransactions;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'bookings', label: 'All Bookings' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'wallet', label: 'Platform Wallet' },
    { id: 'payouts', label: 'Payout Requests' },
    { id: 'agreements', label: 'User Agreements' },
    { id: 'bank', label: 'Bank Details' },
    { id: 'venues', label: 'All Venues', badge: allVenues.filter(v => v.status === 'pending').length || null },
    { id: 'tournaments', label: 'Tournaments', badge: pendingTournamentsCount || null },
    { id: 'kyc', label: 'KYC Verification', badge: (kycList || []).filter(k => k.status === 'pending').length || null },
    { id: 'users', label: 'All Users' },
    { id: 'offers', label: 'All Offers' },
    { id: 'support', label: 'Support Tickets' },
    { id: 'fee_settings', label: '⚙️ Platform Fee Settings' },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: colors.background, fontFamily: "'Inter', sans-serif" }}>

      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          background: toast.type === 'success' ? '#084734' : '#DC2626',
          color: '#CEF17B',
          padding: '14px 20px', borderRadius: '12px',
          fontWeight: '700', fontSize: '0.9rem',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: '10px',
          maxWidth: '360px',
        }}>
          ✓ {toast.msg}
        </div>
      )}

      {/* Dark backdrop overlay on mobile when sidebar is open */}
      {!isDesktop && isMobileSidebarOpen && (
        <div
          onClick={() => setIsMobileSidebarOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 999, WebkitBackdropFilter: 'blur(2px)', backdropFilter: 'blur(2px)', animation: 'fadeIn 0.2s ease-out'
          }}
        />
      )}

      {/* SIDEBAR */}
      <div style={{
        width: '260px', background: colors.cardBg, height: '100vh', position: 'fixed',
        top: 0, left: 0,
        borderRight: `1.5px solid ${colors.border}`, display: 'flex', flexDirection: 'column', padding: '1.5rem 0',
        zIndex: 1000, overflowY: 'auto',
        transform: isDesktop ? 'none' : (isMobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)'),
        transition: 'transform 0.3s ease-in-out',
        boxShadow: isDesktop ? 'none' : '4px 0 24px rgba(0,0,0,0.25)',
      }}>
        <div style={{ padding: '0 24px', marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: '900', color: colors.text, letterSpacing: '-0.5px' }}>TurfX</div>
            <span style={{ fontWeight: '800', fontSize: '0.8rem', color: '#374151', letterSpacing: '1px' }}>ADMIN</span>
          </div>
          {!isDesktop && (
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              style={{ background: 'transparent', border: 'none', color: '#374151', cursor: 'pointer', display: 'flex', padding: '4px' }}
            >
              <X size={20} />
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {tabs.map(t => (
            <div key={t.id} onClick={() => { setTab(t.id); setIsMobileSidebarOpen(false); }} style={{
              display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 24px',
              cursor: 'pointer', color: tab === t.id ? '#084734' : '#374151',
              background: tab === t.id ? '#DCEFB8' : 'transparent',
              borderLeft: `4px solid ${tab === t.id ? '#084734' : 'transparent'}`,
              fontWeight: tab === t.id ? '700' : '500', fontSize: '0.92rem',
              transition: '0.2s all'
            }}>
              {t.label}
              {t.badge ? (
                <span style={{
                  marginLeft: 'auto', background: '#DC2626', color: '#fff',
                  fontSize: '0.7rem', fontWeight: '800', borderRadius: '999px',
                  padding: '2px 7px', minWidth: '18px', textAlign: 'center',
                }}>
                  {t.badge}
                </span>
              ) : null}
            </div>
          ))}
        </div>

        <div style={{ padding: '0 24px' }}>
          <div style={{ borderTop: '1px solid #EEF2E6', paddingTop: '1.2rem' }}>
            <div style={{ color: '#161616', fontSize: '0.8rem', fontWeight: '700', marginBottom: '4px' }}>{user?.name}</div>
            <div style={{ color: '#374151', fontSize: '0.75rem', fontWeight: '600', marginBottom: '1rem' }}>Administrator</div>
            <div onClick={() => { logout(); navigate('/'); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#374151', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer' }}>
              <LogOut size={16} /> Logout
            </div>
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div style={{ flex: 1, marginLeft: isDesktop ? '260px' : 0, width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* TOP BAR */}
        <div style={{ background: 'white', padding: isDesktop ? '1rem 3rem' : '0.8rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1.5px solid #EEF2E6', position: 'sticky', top: 0, zIndex: 90 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {!isDesktop && (
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                style={{
                  background: '#F8FAF7', border: '1.5px solid #EEF2E6',
                  borderRadius: '10px', width: '38px', height: '38px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#084734'
                }}
                aria-label="Open sidebar"
              >
                <Menu size={20} />
              </button>
            )}
            <div>
              <h1 style={{ fontSize: isDesktop ? '1.4rem' : '1.1.rem', fontWeight: '800', color: '#161616', margin: 0 }}>
                {tab === 'overview' && 'Revenue Overview'}
                {tab === 'bookings' && (selectedBooking ? 'Booking Details' : 'All Bookings')}
                {tab === 'transactions' && 'All Transactions'}
                {tab === 'wallet' && 'Platform Wallet'}
                {tab === 'payouts' && 'Payout Requests'}
                {tab === 'agreements' && 'User Agreements & Legal Records'}
                {tab === 'venues' && (selectedVenue ? 'Venue Details' : 'All Venues')}
                {tab === 'tournaments' && 'Tournament Management'}
                {tab === 'kyc' && 'KYC Verification & Partner Identity Documents'}
                {tab === 'users' && 'All Users'}
                {tab === 'offers' && (selectedOffer ? 'Offer Details' : 'All Offers')}
                {tab === 'support' && 'Support Tickets'}
                {tab === 'bank' && 'Admin Bank Details'}
              </h1>
              <p style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '600', marginTop: '2px', display: isDesktop ? 'block' : 'none' }}>
                User fee: {s.userPlatformFeePercent || 5}% · Partner fee: {s.partnerPlatformFeePercent || 5}% · GST: {s.gstRate || 18}% on each fee
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>

            {/* ── NOTIFICATION BELL ── */}
            <div ref={notifRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNotifs(p => !p)}
                style={{
                  position: 'relative', background: showNotifs ? '#DCEFB8' : '#F8FAF7',
                  border: `1.5px solid ${showNotifs ? '#CEF17B' : '#EEF2E6'}`,
                  borderRadius: '12px', padding: '9px 12px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  transition: 'all 0.2s',
                }}
              >
                <Bell size={18} color={showNotifs ? '#084734' : '#374151'} />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: '-6px', right: '-6px',
                    background: '#DC2626', color: '#fff',
                    fontSize: '0.65rem', fontWeight: '800',
                    borderRadius: '999px', padding: '2px 6px',
                    minWidth: '18px', textAlign: 'center',
                    border: '2px solid white',
                  }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* ── DROPDOWN PANEL ── */}
              {showNotifs && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                  width: '380px', background: 'white',
                  borderRadius: '20px', border: '1.5px solid #EEF2E6',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                  zIndex: 999, overflow: 'hidden',
                }}>
                  {/* Header */}
                  <div style={{
                    padding: '1rem 1.25rem', borderBottom: '1.5px solid #EEF2E6',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#F8FAF7',
                  }}>
                    <div>
                      <div style={{ fontWeight: '800', fontSize: '0.95rem', color: '#0f172a' }}>
                        Notifications
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#374151', fontWeight: '600', marginTop: '2px' }}>
                        {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllRead}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px',
                            fontSize: '0.72rem', fontWeight: '700', color: '#084734',
                          }}
                        >
                          <CheckCheck size={14} /> Mark all read
                        </button>
                      )}
                      <button
                        onClick={() => setShowNotifs(false)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#374151', display: 'flex' }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Notification list */}
                  <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '3rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔔</div>
                        <div style={{ fontWeight: '700', color: '#374151', fontSize: '0.9rem' }}>No notifications yet</div>
                        <div style={{ fontWeight: '500', color: '#98A2B3', fontSize: '0.8rem', marginTop: '4px' }}>
                          New events will appear here
                        </div>
                      </div>
                    ) : (
                      notifications.map((n) => {
                        const isRead = readIds.includes(n.id);
                        return (
                          <div
                            key={n.id}
                            onClick={() => {
                              markOneRead(n.id);
                              n.action?.();
                              setShowNotifs(false);
                            }}
                            style={{
                              padding: '0.9rem 1.25rem',
                              borderBottom: '1px solid #F1F5F9',
                              cursor: 'pointer',
                              background: isRead ? 'white' : '#FAFFF5',
                              display: 'flex', gap: '12px', alignItems: 'flex-start',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F8FAF7'}
                            onMouseLeave={e => e.currentTarget.style.background = isRead ? 'white' : '#FAFFF5'}
                          >
                            {/* Icon */}
                            <div style={{
                              width: '34px', height: '34px', borderRadius: '10px',
                              background: n.bg, color: n.color,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0, marginTop: '2px',
                            }}>
                              {n.icon}
                            </div>

                            {/* Content */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontWeight: isRead ? '600' : '800',
                                fontSize: '0.85rem', color: '#0f172a',
                                marginBottom: '3px',
                              }}>
                                {n.title}
                              </div>
                              <div style={{
                                fontSize: '0.78rem', color: '#374151',
                                fontWeight: '500', lineHeight: 1.4,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                {n.message}
                              </div>
                              {n.time && (
                                <div style={{ fontSize: '0.7rem', color: '#98A2B3', fontWeight: '600', marginTop: '4px' }}>
                                  {new Date(n.time).toLocaleString('en-IN', {
                                    day: '2-digit', month: 'short',
                                    hour: '2-digit', minute: '2-digit',
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Unread dot */}
                            {!isRead && (
                              <div style={{
                                width: '8px', height: '8px', borderRadius: '50%',
                                background: '#084734', flexShrink: 0, marginTop: '6px',
                              }} />
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Footer */}
                  {notifications.length > 0 && (
                    <div style={{
                      padding: '0.75rem 1.25rem', borderTop: '1.5px solid #EEF2E6',
                      textAlign: 'center', background: '#F8FAF7',
                    }}>
                      <button
                        onClick={() => { markAllRead(); setShowNotifs(false); }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: '0.78rem', fontWeight: '700', color: '#084734',
                        }}
                      >
                        Dismiss all notifications
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button onClick={fetchData} style={btnSecondary}><RefreshCw size={16} /> Refresh</button>
            <button onClick={handleExportCSV} style={btnPrimary}><Download size={16} /> Export CSV</button>
          </div>
        </div>

        <div style={{ padding: isDesktop ? '2.5rem 3rem' : '1rem 0.8rem' }}>

          {/* -- OVERVIEW TAB -- */}
          {tab === 'overview' && (
            <div>
              {/* DATE FILTERS */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#374151', marginRight: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date Filter:</span>
                {['all', 'today', 'week', 'month', 'year'].map(f => (
                  <button 
                    key={f} 
                    onClick={() => setDateFilter(f)} 
                    style={{
                      padding: '6px 14px', borderRadius: '24px', border: '1.5px solid',
                      borderColor: dateFilter === f ? '#084734' : '#EEF2E6',
                      background: dateFilter === f ? '#DCEFB8' : 'white',
                      color: '#084734',
                      fontWeight: '800', fontSize: '0.78rem', cursor: 'pointer',
                      transition: '0.2s', outline: 'none'
                    }}
                  >
                    {f === 'all' && 'All Time'}
                    {f === 'today' && 'Today'}
                    {f === 'week' && 'Week'}
                    {f === 'month' && 'Month'}
                    {f === 'year' && 'Year'}
                  </button>
                ))}
              </div>
              {/* PAYU GATEWAY TRANSACTIONS CARDS */}
              <div style={{ background: '#F0FDF4', borderRadius: '20px', border: '1.5px solid #BBF7D0', padding: '1.5rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '1.3rem' }}>💳</span>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: '900', color: '#084734', margin: 0 }}>
                      PayU Payment Gateway — Real Transaction Metrics
                    </h3>
                  </div>
                  <span style={{ background: '#DCEFB8', color: '#084734', fontSize: '0.75rem', fontWeight: '800', padding: '4px 12px', borderRadius: '20px' }}>
                    🟢 PayU Gateway Active
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <StatCard
                    label="PayU Total GMV Paid"
                    value={`₹${(s.payuTotalGMV !== undefined ? s.payuTotalGMV : s.totalGMV || 0).toLocaleString()}`}
                    sub={`${s.payuTotalTransactions !== undefined ? s.payuTotalTransactions : s.totalBookings || 0} PayU payments (Total amount paid by customer)`}
                    icon={<Receipt size={22} />}
                    color="#084734"
                  />
                  <StatCard
                    label="PayU Admin Revenue"
                    value={`₹${(s.payuAdminEarnings !== undefined ? s.payuAdminEarnings : s.totalAdminEarnings || 0).toLocaleString()}`}
                    sub="Admin earned platform fees & GST (Customer & Partner)"
                    icon={<IndianRupee size={22} />}
                    color="#10b981"
                  />
                  <StatCard
                    label="PayU Partner Payouts"
                    value={`₹${(s.payuPartnerPayout !== undefined ? s.payuPartnerPayout : s.totalPartnerPayout || 0).toLocaleString()}`}
                    sub="Amount sent to partner (after deleting all fees & taxes)"
                    icon={<TrendingUp size={22} />}
                    color="#0284c7"
                  />
                  <StatCard
                    label="PayU Total Tax (GST)"
                    value={`₹${(s.totalGST || 0).toLocaleString()}`}
                    sub={`Customer Tax ₹${(s.totalUserGST || 0).toLocaleString()} + Vendor Tax ₹${(s.totalPartnerGST || 0).toLocaleString()}`}
                    icon={<ShieldCheck size={22} />}
                    color="#8b5cf6"
                  />
                </div>
              </div>

              {/* TOP STAT CARDS — Row 1: Revenue */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <StatCard label="Total Admin Earnings" value={`₹${(s.totalAdminEarnings || s.totalRevenue || 0).toLocaleString()}`} sub={`${s.totalBookings} confirmed bookings`} icon={<IndianRupee size={22} />} color="#CEF17B" />
                <StatCard label="User Platform Fees" value={`₹${(s.totalUserPlatformFee || s.totalPlatformFee || 0).toLocaleString()}`} sub={`${s.userPlatformFeePercent || 5}% charged to customer`} icon={<Receipt size={22} />} color="#3b82f6" />
                <StatCard label="Partner Platform Fees" value={`₹${(s.totalPartnerPlatformFee || 0).toLocaleString()}`} sub={`${s.partnerPlatformFeePercent || 5}% deducted from partner`} icon={<Percent size={22} />} color="#8b5cf6" />
                <StatCard label="Total GST Collected" value={`₹${(s.totalGST || 0).toLocaleString()}`} sub={`Customer GST ₹${(s.totalUserGST || 0).toLocaleString()} + Vendor GST ₹${(s.totalPartnerGST || 0).toLocaleString()}`} icon={<TrendingUp size={22} />} color="#f59e0b" />
              </div>

              {/* Row 2: GMV + Users */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                <StatCard label="Gross Booking Value (GMV)" value={`₹${(s.totalGMV || 0).toLocaleString()}`} sub="Total amount customer paid" icon={<TrendingUp size={22} />} color="#10b981" />
                <StatCard label="Partner Payouts" value={`₹${(s.totalPartnerPayout || 0).toLocaleString()}`} sub="Net payout after partner fee & vendor tax" icon={<IndianRupee size={22} />} color="#ec4899" />
                <StatCard label="Total Users" value={s.totalUsers || 0} sub="Registered customers" icon={<Users size={22} />} color="#06b6d4" />
                <StatCard label="Total Venues" value={s.totalTurfs || 0} sub="Listed turfs" icon={<MapPin size={22} />} color="#f97316" />
              </div>

              {/* DYNAMIC FEE STRUCTURE BOX */}
              <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', padding: isDesktop ? '2rem' : '1rem', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '0.5rem' }}>
                  <h3 style={{ fontWeight: '800', fontSize: '1rem', color: '#0f172a', margin: 0 }}>
                    Commission Structure — 10% Total Platform Fee (5% Customer + 5% Partner) + 18% GST
                  </h3>
                  <span style={{ background: '#DCEFB8', color: '#084734', fontSize: '0.75rem', fontWeight: '800', padding: '4px 12px', borderRadius: '20px' }}>
                    10% Platform Revenue + Taxes
                  </span>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '600', marginBottom: '1.5rem' }}>
                  5% platform fee paid by customer + 5% platform fee deducted from partner = 10% total fee, plus 18% GST on each fee.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                  <FeeBox label="Customer Fee" amount={`${s.userPlatformFeePercent || 5}%`} desc="Charged to customer on top of base" color="#3b82f6" />
                  <FeeBox label="GST on Customer Fee" amount="18%" desc="18% GST on customer platform fee" color="#f59e0b" />
                  <FeeBox label="Partner Fee" amount={`${s.partnerPlatformFeePercent || 5}%`} desc="Deducted from partner payout" color="#8b5cf6" />
                  <FeeBox label="GST on Partner Fee" amount="18%" desc="18% GST on partner platform fee" color="#f59e0b" />
                  <FeeBox label="Total Admin Revenue" amount="10% Fee + Both Taxes" desc="5% Cust + 5% Part + 18% GST each" color="#084734" highlight />
                </div>

                {/* Example calculations */}
                <div style={{ marginTop: '1.5rem', background: '#F8FAF7', borderRadius: '14px', padding: '1.25rem', border: '1.5px solid #EEF2E6' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>
                    Example — ₹330 booking (5% Customer + 5% Partner = 10% Fee + GST)
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                    {(() => {
                      const base = 330;
                      const uFee = +(base * (s.userPlatformFeePercent || 5) / 100).toFixed(2);
                      const uGst = +(uFee * 0.18).toFixed(2);
                      const pFee = +(base * (s.partnerPlatformFeePercent || 5) / 100).toFixed(2);
                      const pGst = +(pFee * 0.18).toFixed(2);
                      return [
                        { label: 'Customer Pays (GMV)', value: `₹${(base + uFee + uGst).toFixed(2)}`, sub: `₹${base} base + ₹${uFee} fee (5%) + ₹${uGst} GST`, color: '#3b82f6' },
                        { label: 'Partner Receives', value: `₹${(base - pFee - pGst).toFixed(2)}`, sub: `₹${base} base − ₹${pFee} fee (5%) − ₹${pGst} GST`, color: '#10b981' },
                        { label: 'Admin Earns (10% + GST)', value: `₹${(uFee + uGst + pFee + pGst).toFixed(2)}`, sub: `₹${uFee} + ₹${pFee} (10% fee) + ₹${(uGst + pGst).toFixed(2)} GST`, color: '#084734' },
                        { label: 'Total GST (Taxes)', value: `₹${(uGst + pGst).toFixed(2)}`, sub: `Customer GST ₹${uGst} + Vendor GST ₹${pGst}`, color: '#f59e0b' },
                      ].map((item, i) => (
                        <div key={i} style={{ background: 'white', borderRadius: '12px', padding: '1rem', border: `1.5px solid #EEF2E6` }}>
                          <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#374151', marginBottom: '6px', textTransform: 'uppercase' }}>{item.label}</div>
                          <div style={{ fontSize: '1.1rem', fontWeight: '900', color: item.color }}>{item.value}</div>
                          <div style={{ fontSize: '0.68rem', color: '#98A2B3', marginTop: '4px', fontWeight: '600' }}>{item.sub}</div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>

              {/* CHARTS ROW */}
              <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: '1.5rem' }}>

                {/* PIE CHART — Monthly Revenue Split */}
                <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', padding: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h3 style={{ fontWeight: '800', fontSize: '1rem', color: '#0f172a' }}>Monthly Admin Earnings</h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '38% 62%', gap: '1.5rem', alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      {monthly.length > 0 ? (
                        <PieChart
                          size={130}
                          slices={monthly.slice(0, 6).reverse().map((m, i) => ({
                            label: new Date(m.month + '-01').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
                            value: Math.round(m.adminEarnings || m.revenue || 0),
                            color: ['#CEF17B','#3b82f6','#f59e0b','#8b5cf6','#ec4899','#06b6d4'][i % 6],
                          }))}
                        />
                      ) : (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#374151', fontWeight: '600' }}>No data yet</div>
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: '800', fontSize: '0.8rem', color: '#374151', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Select Date
                      </div>
                      <AdminCalendar
                        selectedDate={selectedCalendarDate}
                        onChangeDate={setSelectedCalendarDate}
                        colors={colors}
                      />
                    </div>
                  </div>
                </div>

                {/* PIE CHART — Revenue Composition */}
                <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', padding: '2rem', display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ fontWeight: '800', fontSize: '1rem', color: '#0f172a', marginBottom: '1.5rem' }}>
                    Admin Earnings Composition ({selectedCalendarDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })})
                  </h3>
                  {(() => {
                    const daily = getDailyComposition(selectedCalendarDate);
                    const slices = [
                      { label: 'User Platform Fee', value: Math.round(daily.userPlatformFee), color: '#3b82f6' },
                      { label: 'User GST', value: Math.round(daily.userGST), color: '#93c5fd' },
                      { label: 'Partner Platform Fee', value: Math.round(daily.partnerPlatformFee), color: '#8b5cf6' },
                      { label: 'Partner GST', value: Math.round(daily.partnerGST), color: '#c4b5fd' },
                      { label: 'Tournament Cust. Fee', value: Math.round(daily.tournamentCustomerFee || 0), color: '#10b981' },
                      { label: 'Tournament Partner Fee', value: Math.round(daily.tournamentPartnerFee || 0), color: '#06b6d4' },
                    ].filter(sl => sl.value > 0);

                    const selectedDateFormatted = selectedCalendarDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

                    return (
                      <>
                        <PieChart
                          size={180}
                          slices={slices}
                        />
                        <div style={{ marginTop: '1rem', padding: '12px', background: '#DCEFB8', borderRadius: '12px', border: '1px solid #DCEFB8' }}>
                          <div style={{ fontSize: '0.75rem', color: '#084734', fontWeight: '700' }}>
                            Daily Admin Earnings ({selectedDateFormatted}) = <strong>₹{daily.adminEarnings.toLocaleString()}</strong>
                          </div>
                        </div>

                        {/* Selected Date Admin Earnings Sub-Section */}
                        <div style={{ marginTop: '1.5rem', paddingTop: '1.2rem', borderTop: '1.5px dashed #EEF2E6' }}>
                          <h4 style={{ fontWeight: '800', fontSize: '0.85rem', color: '#0f172a', marginBottom: '0.75rem' }}>
                            Selected Date Earnings ({selectedDateFormatted})
                          </h4>

                          {/* Turf Booking Earnings Sub-Section */}
                          <div style={{ background: '#F8FAF7', borderRadius: '12px', padding: '10px 12px', marginBottom: '10px', border: '1px solid #EEF2E6' }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#084734', textTransform: 'uppercase', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                              <span>⚽ Turf Booking Earnings</span>
                              <span>₹{Math.round(daily.turfAdminEarnings || 0).toLocaleString()}</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', fontSize: '0.72rem', fontWeight: '600', color: '#475569' }}>
                              <div>User Fee: ₹{Math.round(daily.userPlatformFee || 0).toLocaleString()}</div>
                              <div>User GST: ₹{Math.round(daily.userGST || 0).toLocaleString()}</div>
                              <div>Partner Fee: ₹{Math.round(daily.partnerPlatformFee || 0).toLocaleString()}</div>
                              <div>Partner GST: ₹{Math.round(daily.partnerGST || 0).toLocaleString()}</div>
                            </div>
                          </div>

                          {/* Tournament Earnings Sub-Section */}
                          <div style={{ background: '#F0FDF4', borderRadius: '12px', padding: '10px 12px', marginBottom: '10px', border: '1px solid #BBF7D0' }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#065F46', textTransform: 'uppercase', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                              <span>🏆 Tournament Earnings</span>
                              <span>₹{Math.round(daily.tournamentAdminEarnings || 0).toLocaleString()}</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', fontSize: '0.72rem', fontWeight: '600', color: '#047857' }}>
                              <div>Customer Fee: ₹{Math.round(daily.tournamentCustomerFee || 0).toLocaleString()}</div>
                              <div>Partner Fee: ₹{Math.round(daily.tournamentPartnerFee || 0).toLocaleString()}</div>
                            </div>
                          </div>

                          {/* Selected Date Total Earnings */}
                          <div style={{ padding: '10px 14px', background: '#DCEFB8', borderRadius: '12px', border: '1.5px solid #CEF17B', fontSize: '0.8rem', color: '#084734', fontWeight: '800', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Selected Date Total Earnings:</span>
                            <strong style={{ fontSize: '0.95rem' }}>₹{daily.adminEarnings.toLocaleString()}</strong>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* -- MONTHLY TAB -- */}
          {tab === 'monthly' && (
            <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#F8FAF7' }}>
                  <tr>
                    {['Month', 'Bookings', 'Gross Value (GMV)', 'Platform Fee', 'GST Collected', 'Total Revenue', ''].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthly.length > 0 ? monthly.map((m, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #EEF2E6' }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: '800', color: '#0f172a' }}>
                          {new Date(m.month + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                        </div>
                      </td>
                      <td style={tdStyle}><span style={numBadge}>{m.bookings}</span></td>
                      <td style={tdStyle}><span style={{ fontWeight: '700', color: '#8b5cf6' }}>₹{(m.gmv || 0).toLocaleString()}</span></td>
                      <td style={tdStyle}><span style={{ fontWeight: '700', color: '#3b82f6' }}>₹{(m.userPlatformFee || m.platformFee || 0).toLocaleString()}</span></td>
                      <td style={tdStyle}><span style={{ fontWeight: '700', color: '#f59e0b' }}>₹{(m.userGst || m.gst || 0).toLocaleString()}</span></td>
                      <td style={tdStyle}><span style={{ fontWeight: '900', color: '#084734', fontSize: '1rem' }}>₹{(m.adminEarnings || m.revenue || 0).toLocaleString()}</span></td>
                      <td style={tdStyle}>
                        <div style={{ width: '80px', height: '8px', background: '#EEF2E6', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min(((m.adminEarnings || m.revenue || 0) / (monthly[monthly.length - 1]?.revenue || 1)) * 100, 100)}%`, background: '#CEF17B', borderRadius: '4px' }} />
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>No bookings yet</td></tr>
                  )}
                </tbody>
                {monthly.length > 0 && (
                  <tfoot style={{ background: '#DCEFB8', borderTop: '2px solid #DCEFB8' }}>
                    <tr>
                      <td style={{ ...tdStyle, fontWeight: '900', color: '#0f172a' }}>TOTAL</td>
                      <td style={tdStyle}><span style={{ ...numBadge, background: '#DCEFB8', color: '#084734' }}>{s.totalBookings}</span></td>
                      <td style={{ ...tdStyle, fontWeight: '900', color: '#8b5cf6' }}>₹{s.totalGMV?.toLocaleString()}</td>
                      <td style={{ ...tdStyle, fontWeight: '900', color: '#3b82f6' }}>₹{(s.totalUserPlatformFee || s.totalPlatformFee || 0)?.toLocaleString()}</td>
                      <td style={{ ...tdStyle, fontWeight: '900', color: '#f59e0b' }}>₹{(s.totalGST || 0)?.toLocaleString()}</td>
                      <td style={{ ...tdStyle, fontWeight: '900', color: '#084734', fontSize: '1.1rem' }}>₹{(s.totalAdminEarnings || s.totalRevenue || 0)?.toLocaleString()}</td>
                      <td style={tdStyle} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* -- PER TURF TAB -- */}
          {tab === 'turfs' && (
            <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#F8FAF7' }}>
                  <tr>
                    {['#', 'Venue Name', 'Bookings', 'Gross Value', 'Platform Fee', 'GST', 'Revenue'].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {perTurf.length > 0 ? perTurf.map((t, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #EEF2E6' }}>
                      <td style={{ ...tdStyle, color: '#374151', fontWeight: '700' }}>{i + 1}</td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: '800', color: '#0f172a' }}>{t.turfName}</div>
                      </td>
                      <td style={tdStyle}><span style={numBadge}>{t.bookings}</span></td>
                      <td style={{ ...tdStyle, fontWeight: '700', color: '#8b5cf6' }}>₹{(t.gmv || 0).toLocaleString()}</td>
                      <td style={{ ...tdStyle, fontWeight: '700', color: '#3b82f6' }}>₹{(t.userPlatformFee || t.platformFee || 0).toLocaleString()}</td>
                      <td style={{ ...tdStyle, fontWeight: '700', color: '#f59e0b' }}>₹{(t.userGst || t.gst || 0).toLocaleString()}</td>
                      <td style={{ ...tdStyle, fontWeight: '900', color: '#084734' }}>₹{(t.adminEarnings || t.revenue || 0).toLocaleString()}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>No data yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* -- TRANSACTIONS TAB -- */}
          {tab === 'transactions' && (
            <div>
              {/* DATE FILTERS */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#374151', marginRight: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filter by Date:</span>
                {['all', 'today', 'week', 'month', 'year'].map(f => (
                  <button 
                    key={f} 
                    onClick={() => setDateFilter(f)} 
                    style={{
                      padding: '8px 18px', borderRadius: '24px', border: '1.5px solid',
                      borderColor: dateFilter === f ? '#084734' : '#EEF2E6',
                      background: dateFilter === f ? '#DCEFB8' : 'white',
                      color: '#084734',
                      fontWeight: '800', fontSize: '0.8rem', cursor: 'pointer',
                      transition: '0.2s', outline: 'none'
                    }}
                  >
                    {f === 'all' && 'All Time'}
                    {f === 'today' && 'Today'}
                    {f === 'week' && 'Week'}
                    {f === 'month' && 'Month'}
                    {f === 'year' && 'Year'}
                  </button>
                ))}
              </div>

              <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', overflow: 'hidden' }}>
                <div style={{ padding: '1.5rem', borderBottom: '1.5px solid #EEF2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: '800', color: '#0f172a' }}>
                    {dateFilter === 'all' ? 'All' : dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)} Transactions ({transactions.length})
                  </div>
                  <button onClick={handleExportCSV} style={btnPrimary}><Download size={16} /> Export CSV</button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
                    <thead style={{ background: '#F8FAF7' }}>
                      <tr>
                        {['Booking ID', 'Customer', 'Venue', 'Date', 'Turf Price', 'User Fee', 'User GST', 'Partner Fee', 'Partner GST', 'Admin Earns', 'User Pays'].map(h => (
                          <th key={h} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.length > 0 ? transactions.map((t, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #EEF2E6' }}>
                          <td style={tdStyle}>
                            <span style={{ fontFamily: 'monospace', fontWeight: '800', color: '#084734', background: '#DCEFB8', padding: '3px 8px', borderRadius: '6px', fontSize: '0.8rem' }}>
                              #{t.bookingRef}
                            </span>
                            <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#084734', marginTop: '2px', background: '#F0FDF4', padding: '1px 6px', borderRadius: '4px', display: 'inline-block' }}>
                              💳 PayU Gateway
                            </div>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: '700', color: '#0f172a', fontSize: '0.82rem' }}>
                            {t.userName}
                            <div style={{ fontSize: '0.72rem', color: '#374151', fontWeight: '500' }}>{t.userPhone}</div>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: '600', color: '#374151', fontSize: '0.82rem' }}>
                            {t.turfName}
                            <div style={{ fontSize: '0.72rem', color: '#98A2B3' }}>{t.date} · {t.timeSlot}</div>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: '600', color: '#374151', fontSize: '0.82rem' }}>{t.date}</td>
                          <td style={{ ...tdStyle, fontWeight: '700', color: '#374151' }}>₹{(t.baseAmount || t.courtAmount || 0).toLocaleString()}</td>
                          <td style={{ ...tdStyle, fontWeight: '700', color: '#3b82f6' }}>₹{(t.userPlatformFee || t.platformFee || 0).toLocaleString()}</td>
                          <td style={{ ...tdStyle, fontWeight: '700', color: '#93c5fd' }}>₹{(t.userGst || t.gst || 0).toLocaleString()}</td>
                          <td style={{ ...tdStyle, fontWeight: '700', color: '#8b5cf6' }}>₹{(t.partnerPlatformFee || 0).toLocaleString()}</td>
                          <td style={{ ...tdStyle, fontWeight: '700', color: '#c4b5fd' }}>₹{(t.partnerGst || 0).toLocaleString()}</td>
                          <td style={{ ...tdStyle, fontWeight: '800', color: '#084734', background: '#F0FDF4' }}>₹{(t.adminEarnings || 0).toLocaleString()}</td>
                          <td style={{ ...tdStyle, fontWeight: '900', color: '#0f172a' }}>₹{(t.totalPaid || 0).toLocaleString()}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan="11" style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>No transactions yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* -- WALLET TAB -- */}
          {tab === 'wallet' && (
            <AdminWallet />
          )}

          {/* -- PAYOUTS TAB -- */}
          {tab === 'payouts' && (
            <AdminPayouts />
          )}

          {/* -- USER AGREEMENTS TAB -- */}
          {tab === 'agreements' && (
            <AdminAgreements token={token} />
          )}

          {/* -- BANK DETAILS TAB -- */}
          {tab === 'bank' && (
            <div style={{ maxWidth: '680px' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.4rem', fontWeight: '900', color: '#161616', margin: 0 }}>Admin Bank Details</h2>
                <p style={{ color: '#374151', fontSize: '0.88rem', fontWeight: '600', marginTop: '6px' }}>
                  Required for processing platform withdrawals and tracking cash flow.
                </p>
              </div>
              <BankDetailsForm token={token} required={true} />
            </div>
          )}

          {/* -- ALL VENUES TAB -- */}
          {tab === 'venues' && (
            <div>
              {selectedVenue ? (
                /* ── DETAILED VENUE & OWNER PAGE ── */
                <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
                  <button 
                    onClick={() => setSelectedVenue(null)} 
                    style={{ 
                      display: 'flex', alignItems: 'center', gap: '8px', 
                      background: 'none', border: 'none', color: '#084734', 
                      fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer', 
                      marginBottom: '1.5rem', padding: 0 
                    }}
                  >
                    <ArrowLeft size={16} /> Back to Venues
                  </button>

                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '2rem' }}>
                    {/* LEFT PANEL: Venue details */}
                    <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '2rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '1rem' }}>
                        <div>
                          <h2 style={{ fontSize: '1.5rem', fontWeight: '900', color: '#161616', margin: 0 }}>
                            {selectedVenue.name}
                          </h2>
                          <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: '700' }}>
                            ⚽ {selectedVenue.sport || 'General'} · {selectedVenue.type || 'Indoor'}
                          </span>
                        </div>
                        <span style={{ 
                          background: selectedVenue.status === 'pending' ? '#FEF3C7' : selectedVenue.isActive !== false ? '#D1FAE5' : '#FEE2E2',
                          color: selectedVenue.status === 'pending' ? '#92400E' : selectedVenue.isActive !== false ? '#065F46' : '#DC2626',
                          padding: '6px 14px', borderRadius: '10px', fontSize: '0.8rem', fontWeight: '900', textTransform: 'uppercase'
                        }}>
                          {selectedVenue.status === 'pending' ? 'Pending Approval' : selectedVenue.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      {/* Image Gallery */}
                      {selectedVenue.images && selectedVenue.images.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px', marginBottom: '2rem' }}>
                          {selectedVenue.images.map((img, idx) => {
                            const isVideo = img.toLowerCase().endsWith('.mp4') || img.toLowerCase().includes('/video');
                            return isVideo ? (
                              <video 
                                key={idx} 
                                src={img} 
                                controls
                                style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '12px', border: '1px solid #EEF2E6' }} 
                              />
                            ) : (
                              <img 
                                key={idx} 
                                src={img} 
                                alt={`${selectedVenue.name} preview`} 
                                style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '12px', border: '1px solid #EEF2E6' }} 
                              />
                            );
                          })}
                        </div>
                      ) : null}

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Pricing / Rate</div>
                          <div style={{ fontWeight: '900', color: '#084734', fontSize: '1.25rem' }}>
                            {getTurfPriceRange(selectedVenue).text}/hr
                          </div>
                          {getTurfPriceRange(selectedVenue).hasRange && (
                            <div style={{ fontSize: '0.72rem', color: '#64748B', fontWeight: '700', marginTop: '2px' }}>
                              Base Rate: ₹{selectedVenue.price_per_hour}/hr
                            </div>
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Venue Size</div>
                          <div style={{ fontWeight: '800', color: '#161616' }}>{selectedVenue.venueSize || '—'}</div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Surface Type</div>
                          <div style={{ fontWeight: '800', color: '#161616' }}>{selectedVenue.surfaceType || '—'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Booking Type</div>
                          <div style={{ fontWeight: '800', color: '#161616' }}>{selectedVenue.bookingType || '—'}</div>
                        </div>
                      </div>

                      <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Location</div>
                        <div style={{ fontWeight: '700', color: '#161616' }}>
                          {selectedVenue.location}, {selectedVenue.city}, {selectedVenue.state || ''} - {selectedVenue.pincode || ''}
                        </div>
                        {selectedVenue.lat && selectedVenue.lng && (
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px', fontWeight: '600' }}>
                            Coordinates: {selectedVenue.lat}, {selectedVenue.lng}
                          </div>
                        )}
                      </div>

                      {selectedVenue.description && (
                        <div style={{ marginBottom: '1.5rem', borderTop: '1.5px solid #EEF2E6', paddingTop: '1.2rem' }}>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Description</div>
                          <div style={{ fontSize: '0.88rem', color: '#374151', fontWeight: '500', lineHeight: '1.5' }}>{selectedVenue.description}</div>
                        </div>
                      )}

                      {selectedVenue.amenities && selectedVenue.amenities.length > 0 && (
                        <div style={{ borderTop: '1.5px solid #EEF2E6', paddingTop: '1.2rem', marginBottom: '1.5rem' }}>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '10px' }}>Amenities Available</div>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {selectedVenue.amenities.map(a => (
                              <span key={a} style={{ background: '#F8FAF7', color: '#084734', padding: '4px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '700', border: '1px solid #EEF2E6' }}>
                                ✓ {a}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Operational Timings & Configured Slots Section */}
                      <div style={{ borderTop: '1.5px solid #EEF2E6', paddingTop: '1.2rem' }}>
                        <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Clock size={15} color="#084734" /> Operational Hours & Slot Range Pricing
                        </div>

                        {/* Timings summary & Slot Ranges for every configured schedule */}
                        {(() => {
                          const venueTimings = (Array.isArray(selectedVenue.timings) && selectedVenue.timings.length > 0)
                            ? selectedVenue.timings
                            : (Array.isArray(selectedVenue.pricingRules) && selectedVenue.pricingRules.find(r => (r.type === 'slots_config' || r.id === 'turf_slots_config') && Array.isArray(r.timings) && r.timings.length > 0)?.timings)
                            ? selectedVenue.pricingRules.find(r => (r.type === 'slots_config' || r.id === 'turf_slots_config') && Array.isArray(r.timings)).timings
                            : (() => {
                                try {
                                  const localRules = JSON.parse(localStorage.getItem(`turf_pricing_rules_${selectedVenue._id}`) || '[]');
                                  return localRules.find(r => (r.type === 'slots_config' || r.id === 'turf_slots_config') && Array.isArray(r.timings))?.timings || [];
                                } catch (e) {
                                  return [];
                                }
                              })();

                          const hasDynamicTimings = venueTimings && venueTimings.length > 0;

                          if (hasDynamicTimings) {
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '1rem' }}>
                                {venueTimings.map((schedule, schedIdx) => {
                                  const isWeekend = schedule.name?.includes('Weekend') || schedule.name?.includes('Saturday') || schedule.name?.includes('Sunday');
                                  const isCustom = schedule.name === 'Custom Days Range...' || (!schedule.name?.includes('Weekday') && !schedule.name?.includes('Weekend'));
                                  const displayName = schedule.name === 'Custom Days Range...' && schedule.customName ? schedule.customName : (schedule.name || `Schedule ${schedIdx + 1}`);
                                  const schedPrice = parseFloat(schedule.price || selectedVenue.price_per_hour) || 0;
                                  
                                  const slotsList = Array.isArray(schedule.slots) && schedule.slots.length > 0
                                    ? schedule.slots
                                    : generateSlotsFromRange(schedule.open || '06:00 AM', schedule.close || '10:00 PM', schedPrice);

                                  const cardBorder = isWeekend ? '#FCD34D' : isCustom ? '#86EFAC' : '#DCEFB8';
                                  const cardBg = isWeekend 
                                    ? 'linear-gradient(135deg, #FFFFFF 0%, #FFFDF5 100%)' 
                                    : isCustom 
                                    ? 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)'
                                    : 'linear-gradient(135deg, #FFFFFF 0%, #F8FAF7 100%)';
                                  const icon = isWeekend ? '🎉' : isCustom ? '⚡' : '📅';
                                  const badgeColor = isWeekend ? '#92400E' : isCustom ? '#166534' : '#084734';
                                  const badgeBg = isWeekend ? '#FEF3C7' : isCustom ? '#DCFCE7' : '#DCEFB8';
                                  const badgeBorder = isWeekend ? '#FDE68A' : isCustom ? '#86EFAC' : '#CEF17B';

                                  return (
                                    <div key={schedule.id || schedIdx} style={{
                                      background: cardBg,
                                      padding: '1.25rem 1.5rem',
                                      borderRadius: '18px',
                                      border: `1.5px solid ${cardBorder}`,
                                      boxShadow: '0 6px 20px rgba(8, 71, 52, 0.04)'
                                    }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                          <span style={{ fontSize: '1.3rem' }}>{icon}</span>
                                          <div>
                                            <div style={{ fontSize: '0.98rem', fontWeight: '850', color: badgeColor }}>
                                              {displayName}
                                            </div>
                                            <div style={{ fontSize: '0.76rem', color: '#64748B', fontWeight: '700', marginTop: '2px' }}>
                                              ⏰ {schedule.open || '06:00 AM'} - {schedule.close || '10:00 PM'} • Rate: <span style={{ color: badgeColor, fontWeight: '850' }}>₹{schedPrice}/hr</span>
                                            </div>
                                          </div>
                                        </div>
                                        <span style={{ fontSize: '0.78rem', fontWeight: '850', background: badgeBg, color: badgeColor, padding: '4px 12px', borderRadius: '16px', border: `1px solid ${badgeBorder}` }}>
                                          {slotsList.length} Slots
                                        </span>
                                      </div>

                                      {/* Slots grid matching vendor card format */}
                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '8px', width: '100%' }}>
                                        {slotsList.map((slotItem, sIdx) => {
                                          const label = typeof slotItem === 'string' ? slotItem : (slotItem?.label || slotItem?.slot || '');
                                          const isActive = typeof slotItem === 'string' ? true : slotItem?.isActive !== false;
                                          const slotPrice = typeof slotItem === 'object' && slotItem?.price !== undefined ? slotItem.price : schedPrice;

                                          return (
                                            <div key={sIdx} style={{
                                              display: 'flex', flexDirection: 'column', gap: '4px',
                                              background: isActive ? '#FFFFFF' : '#F1F5F9',
                                              border: isActive ? `1.5px solid ${cardBorder}` : '1.5px dashed #94A3B8',
                                              color: isActive ? '#084734' : '#64748B',
                                              padding: '8px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '800',
                                              boxShadow: isActive ? '0 2px 4px rgba(8, 71, 52, 0.03)' : 'none'
                                            }}>
                                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span>{label}</span>
                                                {!isActive && (
                                                  <span style={{ fontSize: '0.72rem' }} title="Freezed">
                                                    ❄️
                                                  </span>
                                                )}
                                              </div>

                                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2px', borderTop: '1px dashed #E2E8F0', paddingTop: '4px' }}>
                                                <span style={{ fontSize: '0.78rem', fontWeight: '900', color: badgeColor, background: badgeBg, padding: '1px 6px', borderRadius: '4px', border: `1px solid ${badgeBorder}` }}>
                                                  ₹{slotPrice}
                                                </span>

                                                <span style={{
                                                  background: isActive ? '#FEF3C7' : '#E2E8F0',
                                                  color: isActive ? '#92400E' : '#64748B',
                                                  borderRadius: '4px', padding: '2px 6px',
                                                  fontSize: '0.7rem', fontWeight: '800'
                                                }}>
                                                  {isActive ? '⏸️ Active' : '❄️ Freezed'}
                                                </span>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }

                          // Fallback to legacy slots config or default slots
                          const rules = selectedVenue.pricingRules || [];
                          let slotsConfig = rules.find(r => r.type === 'slots_config' || r.id === 'turf_slots_config');
                          
                          if (!slotsConfig && selectedVenue._id) {
                            try {
                              const localRules = JSON.parse(localStorage.getItem(`turf_pricing_rules_${selectedVenue._id}`) || '[]');
                              slotsConfig = localRules.find(r => r.type === 'slots_config' || r.id === 'turf_slots_config');
                            } catch (e) {}
                          }

                          const defaultSlotsList = [
                            "06:00 AM - 07:00 AM", "07:00 AM - 08:00 AM", "08:00 AM - 09:00 AM", "09:00 AM - 10:00 AM",
                            "10:00 AM - 11:00 AM", "11:00 AM - 12:00 PM", "12:00 PM - 01:00 PM", "01:00 PM - 02:00 PM",
                            "02:00 PM - 03:00 PM", "03:00 PM - 04:00 PM", "04:00 PM - 05:00 PM", "05:00 PM - 06:00 PM",
                            "06:00 PM - 07:00 PM", "07:00 PM - 08:00 PM", "08:00 PM - 09:00 PM", "09:00 PM - 10:00 PM"
                          ];

                          const weekdayList = slotsConfig?.weekdaySlots || defaultSlotsList;
                          const weekendList = slotsConfig?.weekendSlots || defaultSlotsList;

                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                              {/* Weekday slots */}
                              <div style={{ background: '#F8FAF7', padding: '12px', borderRadius: '12px', border: '1px solid #EEF2E6' }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#084734', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span>📅 Weekday Slots (Mon - Fri)</span>
                                  <span style={{ fontSize: '0.7rem', background: '#DCEFB8', color: '#084734', padding: '2px 8px', borderRadius: '6px', fontWeight: '800' }}>{weekdayList.length} Slots</span>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '8px' }}>
                                  {weekdayList.map((s, idx) => {
                                    const label = typeof s === 'string' ? s : (s?.label || s?.slot || '');
                                    const isActive = typeof s === 'string' ? true : s?.isActive !== false;
                                    const slotPrice = typeof s === 'object' && s?.price !== undefined ? s.price : (selectedVenue.price_per_hour || 0);
                                    return (
                                      <div key={idx} style={{
                                        display: 'flex', flexDirection: 'column', gap: '4px',
                                        background: isActive ? '#FFFFFF' : '#F1F5F9',
                                        border: isActive ? '1.5px solid #DCEFB8' : '1.5px dashed #94A3B8',
                                        color: isActive ? '#084734' : '#64748B',
                                        padding: '8px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '800'
                                      }}>
                                        <span>{label} {!isActive && '❄️'}</span>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2px', borderTop: '1px dashed #E2E8F0', paddingTop: '4px' }}>
                                          <span style={{ fontSize: '0.78rem', fontWeight: '900', color: '#084734', background: '#DCEFB8', padding: '1px 6px', borderRadius: '4px' }}>
                                            ₹{slotPrice}
                                          </span>
                                          <span style={{ fontSize: '0.7rem', color: isActive ? '#92400E' : '#64748B' }}>
                                            {isActive ? '⏸️ Active' : '❄️ Freezed'}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Weekend slots */}
                              <div style={{ background: '#FFFDF5', padding: '12px', borderRadius: '12px', border: '1.5px solid #FCD34D' }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#92400E', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span>🎉 Weekend Slots (Sat - Sun)</span>
                                  <span style={{ fontSize: '0.7rem', background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: '6px', fontWeight: '800' }}>{weekendList.length} Slots</span>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '8px' }}>
                                  {weekendList.map((s, idx) => {
                                    const label = typeof s === 'string' ? s : (s?.label || s?.slot || '');
                                    const isActive = typeof s === 'string' ? true : s?.isActive !== false;
                                    const slotPrice = typeof s === 'object' && s?.price !== undefined ? s.price : (selectedVenue.price_per_hour || 0);
                                    return (
                                      <div key={idx} style={{
                                        display: 'flex', flexDirection: 'column', gap: '4px',
                                        background: isActive ? '#FFFFFF' : '#F1F5F9',
                                        border: isActive ? '1.5px solid #FCD34D' : '1.5px dashed #94A3B8',
                                        color: isActive ? '#92400E' : '#64748B',
                                        padding: '8px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '800'
                                      }}>
                                        <span>{label} {!isActive && '❄️'}</span>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2px', borderTop: '1px dashed #E2E8F0', paddingTop: '4px' }}>
                                          <span style={{ fontSize: '0.78rem', fontWeight: '900', color: '#92400E', background: '#FEF3C7', padding: '1px 6px', borderRadius: '4px' }}>
                                            ₹{slotPrice}
                                          </span>
                                          <span style={{ fontSize: '0.7rem', color: isActive ? '#92400E' : '#64748B' }}>
                                            {isActive ? '⏸️ Active' : '❄️ Freezed'}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* RIGHT PANEL: Owner details */}
                    <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '2rem', height: 'fit-content' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '12px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#DCEFB8', color: '#084734', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '1.1rem' }}>
                          👤
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontWeight: '850', color: '#161616', fontSize: '1rem' }}>Partner / Owner Profile</h4>
                          <span style={{ fontSize: '0.72rem', color: '#98A2B3', fontWeight: '700' }}>Listing Host</span>
                        </div>
                      </div>

                      {(() => {
                        const ownerObj = selectedVenue.owner_id || {};
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                            <div>
                              <span style={{ fontSize: '0.7rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Full Name</span>
                              <div style={{ fontWeight: '800', color: '#161616', marginTop: '2px', fontSize: '0.92rem' }}>
                                {ownerObj.name || selectedVenue.ownerName || '—'}
                              </div>
                            </div>
                            <div>
                              <span style={{ fontSize: '0.7rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Phone Number</span>
                              <div style={{ fontWeight: '800', color: '#161616', marginTop: '2px', fontSize: '0.92rem' }}>
                                {ownerObj.phone || selectedVenue.ownerPhone || '—'}
                              </div>
                            </div>
                            <div>
                              <span style={{ fontSize: '0.7rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Email Address</span>
                              <div style={{ fontWeight: '800', color: '#161616', marginTop: '2px', fontSize: '0.92rem' }}>
                                {ownerObj.email || '—'}
                              </div>
                            </div>
                            {ownerObj._id && (
                              <div style={{ borderTop: '1.5px solid #EEF2E6', paddingTop: '1rem', marginTop: '5px' }}>
                                <span style={{ fontSize: '0.65rem', color: '#98A2B3', fontWeight: '700', textTransform: 'uppercase' }}>Owner ID</span>
                                <div style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#64748b', marginTop: '2px' }}>{ownerObj._id}</div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ) : (
                /* ── CARD GRID VIEW ── */
                <div>
                  {/* Pending approvals banner */}
                  {allVenues.filter(v => v.status === 'pending').length > 0 && (
                    <div style={{
                      background: '#FEF3C7', border: '1.5px solid #FCD34D',
                      borderRadius: '16px', padding: '1rem 1.5rem',
                      marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '12px',
                    }}>
                      <span style={{ fontSize: '1.4rem' }}>⏳</span>
                      <div>
                        <div style={{ fontWeight: '800', color: '#92400E', fontSize: '0.95rem' }}>
                          {allVenues.filter(v => v.status === 'pending').length} venue{allVenues.filter(v => v.status === 'pending').length > 1 ? 's' : ''} pending approval
                        </div>
                      </div>
                    </div>
                  )}

                  {sideLoading ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>Loading venues...</div>
                  ) : allVenues.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
                      {allVenues.map((v) => {
                        const ownerObj = v.owner_id || {};
                        const ownerName = ownerObj.name || v.ownerName || '—';
                        
                        return (
                          <div 
                            key={v._id}
                            onClick={() => setSelectedVenue(v)}
                            style={{
                              background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6',
                              overflow: 'hidden', cursor: 'pointer', transition: '0.2s',
                              display: 'flex', flexDirection: 'column', gap: 0,
                              boxShadow: '0 2px 10px rgba(0,0,0,0.01)'
                            }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = '#CEF17B'}
                            onMouseLeave={e => e.currentTarget.style.borderColor = '#EEF2E6'}
                          >
                            {/* Venue Image Header */}
                            <div style={{ height: '160px', width: '100%', background: '#F8FAF7', position: 'relative' }}>
                              {v.images && v.images.length > 0 ? (
                                <img src={v.images[0]} alt={v.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>🏟️</div>
                              )}
                              {/* Status Badge overlay */}
                              <span style={{ 
                                position: 'absolute', top: '12px', right: '12px',
                                background: v.status === 'pending' ? '#FEF3C7' : v.isActive !== false ? '#D1FAE5' : '#FEE2E2',
                                color: v.status === 'pending' ? '#92400E' : v.isActive !== false ? '#065F46' : '#DC2626',
                                padding: '4px 10px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase'
                              }}>
                                {v.status === 'pending' ? 'Pending' : v.isActive !== false ? 'Active' : 'Inactive'}
                              </span>
                            </div>

                            {/* Card Details */}
                            <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <h4 style={{ margin: 0, fontWeight: '900', color: '#161616', fontSize: '1.1rem' }}>{v.name}</h4>
                                <span style={{ background: '#F8FAF7', color: '#084734', padding: '2px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '800', border: '1px solid #EEF2E6' }}>
                                  {v.sport}
                                </span>
                              </div>

                              <p style={{ margin: '0 0 10px 0', fontSize: '0.8rem', color: '#374151', fontWeight: '600' }}>📍 {v.location}, {v.city}</p>

                              <div style={{ borderTop: '1px solid #F1F5F0', paddingTop: '10px', marginTop: 'auto' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                  <div>
                                    <div style={{ fontSize: '0.65rem', color: '#98A2B3', fontWeight: '700', textTransform: 'uppercase' }}>Host / Owner</div>
                                    <div style={{ fontSize: '0.8rem', fontWeight: '750', color: '#0f172a' }}>{ownerName}</div>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.65rem', color: '#98A2B3', fontWeight: '700', textTransform: 'uppercase' }}>Hourly Rate</div>
                                    <div style={{ fontSize: '0.98rem', fontWeight: '900', color: '#084734' }}>{getTurfPriceRange(v).text}</div>
                                  </div>
                                </div>

                                {/* Actions Row */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #F1F5F0', paddingTop: '10px' }}>
                                  {/* Approve / Reject or Toggle Status */}
                                  {v.status === 'pending' ? (
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            await axios.patch(`${API}/admin/turfs/${v._id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                            fetchVenues();
                                            showAdminToast('Venue approved successfully');
                                          } catch { showAdminToast('Failed to approve venue', 'error'); }
                                        }}
                                        style={{ background: '#D1FAE5', color: '#065F46', border: 'none', padding: '6px 12px', borderRadius: '8px', fontWeight: '800', fontSize: '0.75rem', cursor: 'pointer' }}
                                      >
                                        ✓ Approve
                                      </button>
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            await axios.patch(`${API}/admin/turfs/${v._id}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                            fetchVenues();
                                            showAdminToast('Venue rejected');
                                          } catch { showAdminToast('Failed to reject venue', 'error'); }
                                        }}
                                        style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', padding: '6px 12px', borderRadius: '8px', fontWeight: '800', fontSize: '0.75rem', cursor: 'pointer' }}
                                      >
                                        ✕ Reject
                                      </button>
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontSize: '0.72rem', fontWeight: '800', color: '#374151' }}>
                                        {v.isActive !== false ? 'Active' : 'Inactive'}
                                      </span>
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            await axios.patch(`${API}/admin/turfs/${v._id}/status`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                            fetchVenues();
                                            showAdminToast('Venue status updated');
                                          } catch { showAdminToast('Failed to update venue status', 'error'); }
                                        }}
                                        title={v.isActive !== false ? 'Deactivate Venue' : 'Activate Venue'}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
                                      >
                                        {v.isActive !== false ? (
                                          <ToggleRight size={26} color="#065F46" />
                                        ) : (
                                          <ToggleLeft size={26} color="#98A2B3" />
                                        )}
                                      </button>
                                    </div>
                                  )}

                                  {/* Delete Button */}
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!window.confirm(`Permanently delete venue "${v.name}"?\n\nThis action cannot be undone.`)) return;
                                      try {
                                        await axios.delete(`${API}/admin/turfs/${v._id}`, { headers: { Authorization: `Bearer ${token}` } });
                                        fetchVenues();
                                        showAdminToast('Venue deleted successfully');
                                      } catch { showAdminToast('Failed to delete venue', 'error'); }
                                    }}
                                    title="Delete Venue"
                                    style={{
                                      background: '#FEE2E2', border: 'none', cursor: 'pointer',
                                      padding: '8px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}
                                  >
                                    <Trash2 size={16} color="#DC2626" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ background: 'white', padding: '3rem', textAlign: 'center', borderRadius: '20px', border: '1.5px solid #EEF2E6', color: '#374151', fontWeight: '600' }}>
                      No venues found
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* -- USERS TAB -- */}
          {tab === 'users' && !selectedUser && (
            <div>
              {/* Search & Filters */}
              <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Search by name, email or phone..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  style={{ padding: '10px 16px', borderRadius: '12px', border: '1.5px solid #EEF2E6', fontSize: '0.9rem', fontWeight: '600', outline: 'none', minWidth: '300px', background: 'white' }}
                />

                {/* Role Filter Tabs */}
                <div style={{ display: 'flex', background: '#F1F5F9', padding: '4px', borderRadius: '12px' }}>
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'user', label: 'Customers' },
                    { id: 'owner', label: 'Turf Owners' },
                    { id: 'admin', label: 'Admins' }
                  ].map(tabItem => (
                    <button
                      key={tabItem.id}
                      onClick={() => setRoleFilter(tabItem.id)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '8px',
                        border: 'none',
                        background: roleFilter === tabItem.id ? 'white' : 'transparent',
                        color: roleFilter === tabItem.id ? '#0f172a' : '#64748b',
                        fontWeight: '700',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        boxShadow: roleFilter === tabItem.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        transition: 'all 0.2s',
                      }}
                    >
                      {tabItem.label}
                    </button>
                  ))}
                </div>

                <button onClick={fetchUsers} style={{ background: '#F3F4F6', border: 'none', padding: '10px 16px', borderRadius: '10px', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', color: '#374151' }}>
                  Refresh
                </button>
                <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '600', marginLeft: 'auto' }}>
                  {allUsers.length} total users
                </span>
              </div>

              {/* Bulk Actions Banner */}
              {selectedUserIds.length > 0 && (
                <div style={{
                  background: '#084734',
                  color: 'white',
                  borderRadius: '16px',
                  padding: '12px 20px',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px',
                  boxShadow: '0 8px 20px rgba(8, 71, 52, 0.25)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                      background: '#CEF17B',
                      color: '#084734',
                      fontWeight: '900',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '0.82rem'
                    }}>
                      {selectedUserIds.length} Selected
                    </span>
                    <span style={{ fontSize: '0.88rem', fontWeight: '600' }}>Bulk actions for selected users:</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      disabled={bulkActionLoading}
                      onClick={() => handleBulkStatus(true)}
                      style={{
                        background: '#D1FAE5',
                        color: '#065F46',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '10px',
                        fontWeight: '800',
                        fontSize: '0.8rem',
                        cursor: bulkActionLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Check size={15} /> Activate Selected
                    </button>

                    <button
                      disabled={bulkActionLoading}
                      onClick={() => handleBulkStatus(false)}
                      style={{
                        background: '#FEF3C7',
                        color: '#92400E',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '10px',
                        fontWeight: '800',
                        fontSize: '0.8rem',
                        cursor: bulkActionLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <ToggleLeft size={16} /> Deactivate Selected
                    </button>

                    <button
                      disabled={bulkActionLoading}
                      onClick={handleBulkDelete}
                      style={{
                        background: '#EF4444',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '10px',
                        fontWeight: '800',
                        fontSize: '0.8rem',
                        cursor: bulkActionLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Trash2 size={15} /> Delete Selected
                    </button>

                    <button
                      onClick={() => setSelectedUserIds([])}
                      style={{
                        background: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        border: 'none',
                        padding: '8px 14px',
                        borderRadius: '10px',
                        fontWeight: '700',
                        fontSize: '0.8rem',
                        cursor: 'pointer'
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', overflow: 'hidden' }}>
                {sideLoading ? (
                  <div style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>Loading users...</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    {(() => {
                      const q = userSearch.trim().toLowerCase();
                      let filtered = allUsers;

                      // Apply search filter
                      if (q) {
                        filtered = filtered.filter(u =>
                          (u.name || '').toLowerCase().includes(q) ||
                          (u.email || '').toLowerCase().includes(q) ||
                          (u.phone || '').includes(q)
                        );
                      }

                      // Apply role filter
                      if (roleFilter !== 'all') {
                        filtered = filtered.filter(u => {
                          const uRole = u.role || 'user';
                          return uRole === roleFilter;
                        });
                      }

                      const selectableUsers = filtered.filter(u => u.role !== 'admin');
                      const isAllSelected = selectableUsers.length > 0 && selectableUsers.every(u => selectedUserIds.includes(u._id));

                      return (
                        <>
                          <thead style={{ background: '#F8FAF7' }}>
                            <tr>
                              <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={isAllSelected}
                                  onChange={() => handleSelectAllUsers(filtered)}
                                  style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#084734' }}
                                  title={isAllSelected ? "Deselect all" : "Select all eligible users"}
                                />
                              </th>
                              {['#', 'Name', 'Phone / Email', 'City', 'Role', 'Bookings', 'Status', 'Joined', 'Actions'].map(h => (
                                <th key={h} style={thStyle}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.length === 0 ? (
                              <tr><td colSpan="10" style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>{allUsers.length === 0 ? 'No users found' : 'No results match your search'}</td></tr>
                            ) : (
                              filtered.map((u, i) => {
                                const isSelected = selectedUserIds.includes(u._id);
                                return (
                                  <tr 
                                    key={u._id} 
                                    onClick={() => setSelectedUser(u)}
                                    style={{ 
                                      borderBottom: '1px solid #EEF2E6', 
                                      opacity: u.is_active === false ? 0.55 : 1,
                                      cursor: 'pointer',
                                      transition: 'background 0.2s',
                                      background: isSelected ? '#F2F9F2' : 'transparent',
                                    }}
                                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#F8FAF7'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = isSelected ? '#F2F9F2' : 'transparent'; }}
                                  >
                                    <td style={{ ...tdStyle, textAlign: 'center', width: '40px' }} onClick={e => e.stopPropagation()}>
                                      {u.role !== 'admin' ? (
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={(e) => handleToggleSelectUser(u._id, e)}
                                          style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#084734' }}
                                        />
                                      ) : (
                                        <span style={{ color: '#D1D5DB' }}>—</span>
                                      )}
                                    </td>

                                    <td style={{ ...tdStyle, color: '#374151', fontWeight: '700' }}>{i + 1}</td>

                                    <td style={tdStyle}>
                                      <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.88rem' }}>{u.name || '—'}</div>
                                      <div style={{ fontSize: '0.72rem', color: '#374151', fontWeight: '500', marginTop: '2px' }}>ID: {u._id?.slice(-8)?.toUpperCase()}</div>
                                    </td>

                                    <td style={tdStyle}>
                                      <div style={{ fontSize: '0.83rem', color: '#374151', fontWeight: '600' }}>{u.phone || '—'}</div>
                                      <div style={{ fontSize: '0.75rem', color: '#98A2B3', fontWeight: '500' }}>{u.email || '—'}</div>
                                    </td>

                                    <td style={tdStyle}>
                                      <div style={{ fontSize: '0.83rem', color: '#374151', fontWeight: '700' }}>{getUserCity(u)}</div>
                                    </td>

                                    <td style={tdStyle}>
                                      <span style={{
                                        background: u.role === 'admin' ? '#EDE9FE' : u.role === 'owner' ? '#DCEFB8' : '#F0F9FF',
                                        color: u.role === 'admin' ? '#6D28D9' : u.role === 'owner' ? '#065F46' : '#0369A1',
                                        padding: '3px 10px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: '800', textTransform: 'capitalize',
                                      }}>{u.role === 'owner' ? 'Turf Owner' : (u.role || 'user')}</span>
                                    </td>

                            <td style={{ ...tdStyle, fontWeight: '700', color: '#374151' }}>
                              {u.totalBookings || 0}
                            </td>

                            <td style={tdStyle}>
                              <span style={{
                                background: u.is_active !== false ? '#D1FAE5' : '#FEE2E2',
                                color: u.is_active !== false ? '#065F46' : '#DC2626',
                                padding: '3px 10px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: '800',
                              }}>
                                {u.is_active !== false ? 'Active' : 'Inactive'}
                              </span>
                            </td>

                            <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#374151', fontWeight: '600' }}>
                              {u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN') : '—'}
                            </td>

                            <td style={tdStyle}>
                              {u.role !== 'admin' && (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  {/* Toggle Active/Inactive */}
                                  <button
                                    title={u.is_active !== false ? 'Deactivate user' : 'Activate user'}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await axios.patch(`${API}/admin/users/${u._id}/status`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                        fetchUsers();
                                        showAdminToast(`User ${u.is_active !== false ? 'deactivated' : 'activated'} successfully`);
                                      } catch { showAdminToast('Failed to update user status', 'error'); }
                                    }}
                                    style={{
                                      background: u.is_active !== false ? '#FEF3C7' : '#D1FAE5',
                                      color: u.is_active !== false ? '#92400E' : '#065F46',
                                      border: 'none', padding: '5px 10px', borderRadius: '8px',
                                      fontWeight: '700', fontSize: '0.75rem', cursor: 'pointer',
                                    }}
                                  >
                                    {u.is_active !== false ? 'Deactivate' : 'Activate'}
                                  </button>

                                  {/* Delete user */}
                                  <button
                                    title="Permanently remove this user"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!window.confirm(`Remove user "${u.name}"?\n\nThis will deactivate and anonymise their account. Their booking history will be preserved for audit purposes.`)) return;
                                      try {
                                        await axios.delete(`${API}/admin/users/${u._id}`, { headers: { Authorization: `Bearer ${token}` } });
                                        fetchUsers();
                                        showAdminToast(`User "${u.name}" removed successfully`);
                                      } catch (err) {
                                        showAdminToast(err.response?.data?.msg || 'Failed to remove user', 'error');
                                      }
                                    }}
                                    style={{
                                      background: '#FEE2E2', color: '#DC2626',
                                      border: 'none', padding: '5px 10px', borderRadius: '8px',
                                      fontWeight: '700', fontSize: '0.75rem', cursor: 'pointer',
                                    }}
                                  >
                                    🗑 Remove
                                  </button>
                                </div>
                              )}
                              {u.role === 'admin' && (
                                <span style={{ fontSize: '0.72rem', color: '#374151', fontWeight: '600' }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </>
              );
            })()}
          </table>
        )}
              </div>
            </div>
          )}

          {/* -- USER DETAILS VIEW -- */}
          {tab === 'users' && selectedUser && (
            <div style={{ background: 'white', borderRadius: '24px', padding: '2.5rem', border: '1.5px solid #EEF2E6', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
              {/* Back Button */}
              <button 
                onClick={() => setSelectedUser(null)} 
                style={{ 
                  background: 'none', border: 'none', color: '#084734', 
                  fontWeight: '800', fontSize: '0.95rem', cursor: 'pointer', 
                  display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2rem',
                  padding: 0
                }}
              >
                ← Back to Users
              </button>

              {/* Title & Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1.5px solid #F1F5F9', paddingBottom: '1.5rem', marginBottom: '2rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                    <h2 style={{ fontSize: '1.8rem', fontWeight: '900', color: '#0f172a' }}>{selectedUser.name || 'Unnamed User'}</h2>
                    <span style={{
                      background: selectedUser.role === 'admin' ? '#EDE9FE' : selectedUser.role === 'owner' ? '#DCEFB8' : '#F0F9FF',
                      color: selectedUser.role === 'admin' ? '#6D28D9' : selectedUser.role === 'owner' ? '#065F46' : '#0369A1',
                      padding: '4px 12px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '800',
                    }}>
                      {selectedUser.role === 'owner' ? 'Turf Owner' : (selectedUser.role || 'user')}
                    </span>
                  </div>
                  <p style={{ color: '#64748b', fontSize: '0.88rem', fontWeight: '500' }}>User ID: {selectedUser._id}</p>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  {selectedUser.role !== 'admin' && (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            await axios.patch(`${API}/admin/users/${selectedUser._id}/status`, {}, { headers: { Authorization: `Bearer ${token}` } });
                            const updatedUser = { ...selectedUser, is_active: selectedUser.is_active === false };
                            setSelectedUser(updatedUser);
                            fetchUsers();
                            showAdminToast(`User ${updatedUser.is_active ? 'activated' : 'deactivated'} successfully`);
                          } catch { showAdminToast('Failed to update user status', 'error'); }
                        }}
                        style={{
                          background: selectedUser.is_active !== false ? '#FEF3C7' : '#D1FAE5',
                          color: selectedUser.is_active !== false ? '#92400E' : '#065F46',
                          border: 'none', padding: '10px 18px', borderRadius: '12px',
                          fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer',
                        }}
                      >
                        {selectedUser.is_active !== false ? 'Deactivate Account' : 'Activate Account'}
                      </button>

                      <button
                        onClick={async () => {
                          if (!window.confirm(`Permanently remove user "${selectedUser.name}"?\n\nThis will deactivate and anonymise their account.`)) return;
                          try {
                            await axios.delete(`${API}/admin/users/${selectedUser._id}`, { headers: { Authorization: `Bearer ${token}` } });
                            fetchUsers();
                            setSelectedUser(null);
                            showAdminToast(`User "${selectedUser.name}" removed successfully`);
                          } catch (err) {
                            showAdminToast(err.response?.data?.msg || 'Failed to remove user', 'error');
                          }
                        }}
                        style={{
                          background: '#FEE2E2', color: '#DC2626',
                          border: 'none', padding: '10px 18px', borderRadius: '12px',
                          fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer',
                        }}
                      >
                        🗑 Remove User
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Grid Content */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                {/* Account Details */}
                <div style={{ background: '#F8FAF7', borderRadius: '18px', padding: '1.5rem', border: '1.5px solid #EEF2E6' }}>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#0f172a', marginBottom: '1.25rem' }}>Account Details</h3>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span style={{ color: '#64748b', fontWeight: '600' }}>Email Address</span>
                      <span style={{ color: '#0f172a', fontWeight: '700' }}>{selectedUser.email || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span style={{ color: '#64748b', fontWeight: '600' }}>Phone Number</span>
                      <span style={{ color: '#0f172a', fontWeight: '700' }}>{selectedUser.phone || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span style={{ color: '#64748b', fontWeight: '600' }}>Joined Date</span>
                      <span style={{ color: '#0f172a', fontWeight: '700' }}>{selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleString('en-IN') : '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span style={{ color: '#64748b', fontWeight: '600' }}>Status</span>
                      <span style={{ color: selectedUser.is_active !== false ? '#10B981' : '#EF4444', fontWeight: '800' }}>
                        {selectedUser.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span style={{ color: '#64748b', fontWeight: '600' }}>Total Bookings Made</span>
                      <span style={{ color: '#0f172a', fontWeight: '800' }}>{selectedUser.totalBookings || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                <div style={{ background: '#F8FAF7', borderRadius: '18px', padding: '1.5rem', border: '1.5px solid #EEF2E6' }}>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#0f172a', marginBottom: '1.25rem' }}>Bank Account Details</h3>
                  
                  {selectedUser.bankDetails ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {selectedUser.bankDetails.upiId && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                          <span style={{ color: '#64748b', fontWeight: '600' }}>UPI ID</span>
                          <span style={{ color: '#084734', fontWeight: '800', fontFamily: 'monospace' }}>{selectedUser.bankDetails.upiId}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                        <span style={{ color: '#64748b', fontWeight: '600' }}>Account Holder</span>
                        <span style={{ color: '#0f172a', fontWeight: '700' }}>{selectedUser.bankDetails.accountHolderName}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                        <span style={{ color: '#64748b', fontWeight: '600' }}>Bank Name</span>
                        <span style={{ color: '#0f172a', fontWeight: '700' }}>{selectedUser.bankDetails.bankName}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                        <span style={{ color: '#64748b', fontWeight: '600' }}>Account Number</span>
                        <span style={{ color: '#0f172a', fontWeight: '700', fontFamily: 'monospace', fontSize: '0.95rem' }}>{selectedUser.bankDetails.accountNumber}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                        <span style={{ color: '#64748b', fontWeight: '600' }}>IFSC Code</span>
                        <span style={{ color: '#0f172a', fontWeight: '700', fontFamily: 'monospace' }}>{selectedUser.bankDetails.ifscCode}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                        <span style={{ color: '#64748b', fontWeight: '600' }}>Account Type</span>
                        <span style={{ color: '#0f172a', fontWeight: '700', textTransform: 'capitalize' }}>{selectedUser.bankDetails.accountType}</span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#64748b', fontWeight: '600', fontSize: '0.9rem' }}>
                      🚫 No bank details registered on file.
                    </div>
                  )}
                </div>

                {/* KYC & Identity Document Details */}
                {(selectedUser.role === 'owner' || selectedUser.kyc || selectedUser.kyc_status || selectedUser.document_type) && (() => {
                  const matchingKyc = (kycList || []).find(k => 
                    String(k.user_id?._id || k.user_id?.id || k.user_id || k.owner_id?._id || k.owner_id?.id || k.owner_id || '') === String(selectedUser._id || selectedUser.id || '') ||
                    (k.user_email && selectedUser.email && k.user_email.toLowerCase() === selectedUser.email.toLowerCase()) ||
                    (k.user_phone && selectedUser.phone && k.user_phone.replace(/\D/g,'') === selectedUser.phone.replace(/\D/g,''))
                  );
                  const kycStatus = selectedUser.kyc?.status || selectedUser.kyc_status || matchingKyc?.status || 'not_submitted';
                  const docType = selectedUser.kyc?.document_type || selectedUser.document_type || matchingKyc?.document_type;
                  const docNumber = selectedUser.kyc?.document_number || selectedUser.document_number || matchingKyc?.document_number;
                  const docImage = selectedUser.kyc?.document_image || selectedUser.document_image || matchingKyc?.document_image;
                  const businessName = selectedUser.business_name || selectedUser.kyc?.business_name || matchingKyc?.business_name;
                  const panNumber = selectedUser.pan_number || selectedUser.kyc?.pan_number || matchingKyc?.pan_number;

                  return (
                    <div style={{ background: '#F8FAF7', borderRadius: '18px', padding: '1.5rem', border: '1.5px solid #EEF2E6' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                        <h3 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>KYC & Identity</h3>
                        <span style={{
                          padding: '4px 10px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: '800', textTransform: 'uppercase',
                          background: kycStatus === 'approved' ? '#D1FAE5' : kycStatus === 'rejected' ? '#FEE2E2' : kycStatus === 'pending' ? '#FEF3C7' : '#F1F5F9',
                          color: kycStatus === 'approved' ? '#065F46' : kycStatus === 'rejected' ? '#DC2626' : kycStatus === 'pending' ? '#92400E' : '#64748B',
                        }}>
                          {kycStatus === 'pending' ? 'Pending Review' : kycStatus === 'approved' ? 'Approved' : kycStatus === 'rejected' ? 'Rejected' : 'Not Submitted'}
                        </span>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                          <span style={{ color: '#64748b', fontWeight: '600' }}>Document Type</span>
                          <span style={{ color: '#084734', fontWeight: '800' }}>{docType || '—'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                          <span style={{ color: '#64748b', fontWeight: '600' }}>Document Number</span>
                          <span style={{ color: '#0f172a', fontWeight: '800', fontFamily: 'monospace' }}>{docNumber || '—'}</span>
                        </div>
                        {businessName && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                            <span style={{ color: '#64748b', fontWeight: '600' }}>Business Name</span>
                            <span style={{ color: '#0f172a', fontWeight: '700' }}>{businessName}</span>
                          </div>
                        )}
                        {panNumber && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                            <span style={{ color: '#64748b', fontWeight: '600' }}>PAN Number</span>
                            <span style={{ color: '#0f172a', fontWeight: '700', fontFamily: 'monospace' }}>{panNumber}</span>
                          </div>
                        )}

                        {/* Photo preview */}
                        <div style={{ marginTop: '6px', paddingTop: '10px', borderTop: '1px dashed #E2E8F0' }}>
                          <span style={{ color: '#64748b', fontWeight: '600', fontSize: '0.82rem', display: 'block', marginBottom: '8px' }}>Identity Document Photo</span>
                          {docImage ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <img
                                src={getDocUrl(docImage)}
                                alt="KYC Document"
                                onClick={() => setPreviewKycImage({
                                  url: getDocUrl(docImage),
                                  title: `${selectedUser.name || 'Partner'} - ${docType || 'KYC Document'}`,
                                  number: docNumber
                                })}
                                style={{
                                  width: '70px', height: '52px', objectFit: 'cover', borderRadius: '8px',
                                  border: '2px solid #DCEFB8', cursor: 'pointer'
                                }}
                              />
                              <button
                                onClick={() => setPreviewKycImage({
                                  url: getDocUrl(docImage),
                                  title: `${selectedUser.name || 'Partner'} - ${docType || 'KYC Document'}`,
                                  number: docNumber
                                })}
                                style={{
                                  background: '#084734', color: '#CEF17B', border: 'none', padding: '6px 12px',
                                  borderRadius: '8px', fontWeight: '800', fontSize: '0.75rem', cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', gap: '4px'
                                }}
                              >
                                <Eye size={13} /> View Photo
                              </button>
                            </div>
                          ) : (
                            <div style={{ color: '#94a3b8', fontSize: '0.82rem', fontStyle: 'italic' }}>
                              No document photo uploaded
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Turf Partner Wallet Info (Owners only) */}
              {selectedUser.role === 'owner' && (
                <div style={{ marginTop: '2.5rem', borderTop: '1.5px solid #EEF2E6', paddingTop: '2rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '950', color: '#0f172a', marginBottom: '1.5rem' }}>Partner Wallet & Earnings</h3>
                  
                  {walletLoading ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>Loading wallet details...</div>
                  ) : (
                    <div>
                      {/* Wallet Balance & Manual Credit Tool Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '2rem', marginBottom: '2rem' }}>
                        {/* Balance Card */}
                        <div style={{
                          background: 'linear-gradient(135deg, #084734 0%, #052e22 100%)',
                          borderRadius: '24px', padding: '2rem', color: 'white',
                          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                          boxShadow: '0 8px 30px rgba(8, 71, 52, 0.15)', minHeight: '180px'
                        }}>
                          <div>
                            <span style={{ fontSize: '0.8rem', fontWeight: '700', textTransform: 'uppercase', opacity: 0.8, letterSpacing: '0.5px' }}>Partner Wallet Balance</span>
                            <h2 style={{ fontSize: '2.5rem', fontWeight: '900', color: '#CEF17B', marginTop: '10px', marginBottom: '4px' }}>
                              ₹{(selectedUserWallet?.balance || 0).toLocaleString()}
                            </h2>
                          </div>
                          <div style={{ fontSize: '0.75rem', opacity: 0.7, fontWeight: '600' }}>
                            Settled earnings available for partner withdrawal
                          </div>
                        </div>

                        {/* Manual Credit Form */}
                        <div style={{ background: '#F8FAF7', borderRadius: '24px', padding: '2rem', border: '1.5px solid #EEF2E6' }}>
                          <h4 style={{ margin: '0 0 10px 0', fontWeight: '900', color: '#0f172a', fontSize: '1rem' }}>Manually Add Money / Payout</h4>
                          <p style={{ margin: '0 0 1.25rem 0', fontSize: '0.78rem', color: '#374151', fontWeight: '600' }}>
                            Directly credit this partner's wallet if a payout transaction was missed or requires manual adjustments.
                          </p>

                          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <input
                                type="number"
                                placeholder="Amount (₹)"
                                value={manualCreditAmount}
                                onChange={e => setManualCreditAmount(e.target.value)}
                                style={{
                                  width: '100%', padding: '10px 16px', borderRadius: '12px',
                                  border: '1.5px solid #EEF2E6', fontSize: '0.88rem', fontWeight: '700',
                                  outline: 'none', background: 'white'
                                }}
                              />
                            </div>
                            <div style={{ flex: 2 }}>
                              <input
                                type="text"
                                placeholder="Reason (e.g. Manual payout adjustment)"
                                value={manualCreditDesc}
                                onChange={e => setManualCreditDesc(e.target.value)}
                                style={{
                                  width: '100%', padding: '10px 16px', borderRadius: '12px',
                                  border: '1.5px solid #EEF2E6', fontSize: '0.88rem', fontWeight: '700',
                                  outline: 'none', background: 'white'
                                }}
                              />
                            </div>
                          </div>

                          <button
                            onClick={handleManualCredit}
                            disabled={manualCreditLoading}
                            style={{
                              width: '100%', padding: '12px', borderRadius: '12px',
                              background: '#084734', color: '#CEF17B', border: 'none',
                              fontWeight: '900', fontSize: '0.85rem', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                              transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = 0.9}
                            onMouseLeave={e => e.currentTarget.style.opacity = 1}
                          >
                            {manualCreditLoading ? 'Sending...' : '💸 Send to Partner Wallet'}
                          </button>
                        </div>
                      </div>

                      {/* Transaction History Table */}
                      <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', overflow: 'hidden' }}>
                        <div style={{ padding: '1.25rem 1.5rem', background: '#F8FAF7', borderBottom: '1.5px solid #EEF2E6' }}>
                          <h4 style={{ margin: 0, fontWeight: '900', color: '#0f172a', fontSize: '0.92rem' }}>Wallet Transactions Ledger</h4>
                        </div>
                        
                        <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#FAFFFD', position: 'sticky', top: 0, zIndex: 1, borderBottom: '1px solid #EEF2E6' }}>
                              <tr>
                                {['Date / Time', 'Type', 'Description', 'Booking', 'Amount', 'Status'].map(h => (
                                  <th key={h} style={{ ...thStyle, fontSize: '0.72rem', padding: '10px 18px', textAlign: 'left', fontWeight: '850', color: '#374151' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(!selectedUserWallet?.transactions || selectedUserWallet.transactions.length === 0) ? (
                                <tr>
                                  <td colSpan="6" style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600', fontSize: '0.85rem' }}>
                                    No transactions recorded yet.
                                  </td>
                                </tr>
                              ) : (
                                selectedUserWallet.transactions.map((t, idx) => {
                                  const isOutflow = ['debit', 'withdrawal', 'partner_fee_debit', 'cancellation_refund_debit', 'partner_payout_reversal'].includes(t.type);
                                  return (
                                    <tr key={t.id} style={{ borderBottom: '1px solid #EEF2E6', background: idx % 2 === 0 ? 'white' : '#FAFFFE' }}>
                                      <td style={{ ...tdStyle, padding: '10px 18px' }}>
                                        <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#0f172a' }}>
                                          {t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-IN') : '—'}
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: '#374151', marginTop: '2px' }}>
                                          {t.createdAt ? new Date(t.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                                        </div>
                                      </td>
                                      <td style={{ ...tdStyle, padding: '10px 18px' }}>
                                        <span style={{
                                          background: isOutflow ? '#FEE2E2' : '#D1FAE5',
                                          color: isOutflow ? '#DC2626' : '#065F46',
                                          padding: '2px 8px', borderRadius: '6px', fontSize: '0.68rem', fontWeight: '800', textTransform: 'uppercase'
                                        }}>{t.type.replace('_', ' ')}</span>
                                      </td>
                                      <td style={{ ...tdStyle, padding: '10px 18px', fontSize: '0.8rem', fontWeight: '600', color: '#374151', maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.description}>
                                        {t.description}
                                      </td>
                                      <td style={{ ...tdStyle, padding: '10px 18px', fontSize: '0.75rem', fontFamily: 'monospace', color: '#64748b' }}>
                                        {t.bookingRef ? `#${t.bookingRef.toUpperCase()}` : '—'}
                                      </td>
                                      <td style={{ ...tdStyle, padding: '10px 18px', fontSize: '0.88rem', fontWeight: '900', color: isOutflow ? '#dc2626' : '#16a34a' }}>
                                        {isOutflow ? '-' : '+'}₹{t.amount.toLocaleString()}
                                      </td>
                                      <td style={{ ...tdStyle, padding: '10px 18px' }}>
                                        <span style={{
                                          background: t.status === 'success' ? '#D1FAE5' : t.status === 'pending' ? '#FEF3C7' : '#FEE2E2',
                                          color: t.status === 'success' ? '#065F46' : t.status === 'pending' ? '#92400E' : '#DC2626',
                                          padding: '2px 8px', borderRadius: '6px', fontSize: '0.68rem', fontWeight: '800'
                                        }}>{(t.status || 'success').toUpperCase()}</span>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Turf Venues section for Owners */}
              {selectedUser.role === 'owner' && (
                <div style={{ marginTop: '2.5rem' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '900', color: '#0f172a', marginBottom: '1.25rem' }}>Venues Added ({selectedUser.totalVenues || 0})</h3>
                  
                  {(() => {
                    const ownerVenues = allVenues.filter(v => 
                      v.owner_id?._id === selectedUser._id || 
                      v.owner_id?.id === selectedUser._id || 
                      v.owner_id === selectedUser._id
                    );

                    if (ownerVenues.length === 0) {
                      return (
                        <div style={{ background: '#F8FAF7', borderRadius: '18px', border: '1.5px dashed #EEF2E6', padding: '2.5rem', textAlign: 'center', color: '#64748b', fontWeight: '600' }}>
                          No venues registered under this owner yet.
                        </div>
                      );
                    }

                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem' }}>
                        {ownerVenues.map(v => (
                          <div key={v._id} style={{ background: 'white', borderRadius: '18px', border: '1.5px solid #EEF2E6', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>{v.name}</h4>
                              <span style={{
                                background: v.status === 'active' ? '#D1FAE5' : '#FEF3C7',
                                color: v.status === 'active' ? '#065F46' : '#92400E',
                                padding: '2px 8px', borderRadius: '6px', fontSize: '0.68rem', fontWeight: '800', textTransform: 'uppercase'
                              }}>{v.status}</span>
                            </div>
                            
                            <p style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: '500', margin: 0 }}>📍 {v.location}, {v.city}</p>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #F1F5F9', paddingTop: '8px', marginTop: '4px' }}>
                              <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '700' }}>{v.sport}</span>
                              <span style={{ fontSize: '0.85rem', color: '#084734', fontWeight: '800' }}>{getTurfPriceRange(v).text}/hr</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Booked Turfs section */}
              <div style={{ marginTop: '2.5rem', borderTop: '1.5px solid #EEF2E6', paddingTop: '2rem' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '900', color: '#0f172a', marginBottom: '1.25rem' }}>
                  Bookings History ({data?.allBookings?.filter(b => b.user_id?._id === selectedUser._id || b.user_id?.id === selectedUser._id || b.user_id === selectedUser._id).length || 0})
                </h3>
                
                {(() => {
                  const userBookings = data?.allBookings?.filter(b => 
                    b.user_id?._id === selectedUser._id || 
                    b.user_id?.id === selectedUser._id || 
                    b.user_id === selectedUser._id
                  ) || [];

                  if (userBookings.length === 0) {
                    return (
                      <div style={{ background: '#F8FAF7', borderRadius: '18px', border: '1.5px dashed #EEF2E6', padding: '2.5rem', textAlign: 'center', color: '#64748b', fontWeight: '600' }}>
                        No bookings made by this user yet.
                      </div>
                    );
                  }

                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem' }}>
                      {userBookings.map(b => (
                        <div key={b._id} style={{ background: 'white', borderRadius: '18px', border: '1.5px solid #EEF2E6', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h4 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>{b.turf_name || 'Venue'}</h4>
                            <span style={{
                              background: b.status === 'confirmed' || b.status === 'completed' || b.status === 'checked-in' ? '#D1FAE5' : '#FEE2E2',
                              color: b.status === 'confirmed' || b.status === 'completed' || b.status === 'checked-in' ? '#065F46' : '#DC2626',
                              padding: '2px 8px', borderRadius: '6px', fontSize: '0.68rem', fontWeight: '800', textTransform: 'uppercase'
                            }}>{b.status}</span>
                          </div>
                          
                          <p style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: '500', margin: 0 }}>📍 {b.turf_location || 'Location'}, {b.turf_city || 'City'}</p>
                          <div style={{ fontSize: '0.78rem', color: '#374151', fontWeight: '650', marginTop: '4px' }}>
                            📅 {b.date ? new Date(b.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: '#374151', fontWeight: '650' }}>
                            🕒 {b.time_slot || (b.time_slots || [])[0] || '—'}
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #F1F5F9', paddingTop: '8px', marginTop: '4px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '700' }}>{b.sport || 'General'}</span>
                            <span style={{ fontSize: '0.85rem', color: '#084734', fontWeight: '800' }}>₹{b.total_price}/-</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* -- ALL BOOKINGS TAB -- */}
          {tab === 'bookings' && (
            <div>
              {selectedBooking ? (
                /* ── DETAILED BOOKING PAGE ── */
                <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
                  <button 
                    onClick={() => setSelectedBooking(null)} 
                    style={{ 
                      display: 'flex', alignItems: 'center', gap: '8px', 
                      background: 'none', border: 'none', color: '#084734', 
                      fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer', 
                      marginBottom: '1.5rem', padding: 0 
                    }}
                  >
                    <ArrowLeft size={16} /> Back to Bookings
                  </button>

                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '2rem' }}>
                    {/* LEFT PANEL: Booking & Pricing details */}
                    <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '2rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '1rem' }}>
                        <div>
                          <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Booking Reference</span>
                          <h2 style={{ fontSize: '1.5rem', fontWeight: '900', color: '#161616', margin: '4px 0 0 0' }}>
                            #{selectedBooking.booking_id?.toUpperCase() || selectedBooking._id?.slice(-8).toUpperCase()}
                          </h2>
                        </div>
                        <span style={{ 
                          background: selectedBooking.status === 'confirmed' || selectedBooking.status === 'completed' || selectedBooking.status === 'checked-in' ? '#D1FAE5' : '#FEF3C7',
                          color: selectedBooking.status === 'confirmed' || selectedBooking.status === 'completed' || selectedBooking.status === 'checked-in' ? '#065F46' : '#92400E',
                          padding: '6px 14px', borderRadius: '10px', fontSize: '0.8rem', fontWeight: '900', textTransform: 'uppercase'
                        }}>
                          {selectedBooking.status}
                        </span>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Date</div>
                          <div style={{ fontWeight: '800', color: '#161616', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Calendar size={16} color="#084734" />
                            {selectedBooking.date ? new Date(selectedBooking.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Time Slots</div>
                          <div style={{ fontWeight: '800', color: '#161616', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Clock size={16} color="#084734" />
                            {(selectedBooking.time_slots || [selectedBooking.time_slot]).join(', ')}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Sport</div>
                          <div style={{ fontWeight: '800', color: '#161616' }}>{selectedBooking.sport || selectedBooking.turf_id?.sport || 'General'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Created At</div>
                          <div style={{ fontWeight: '800', color: '#161616' }}>
                            {selectedBooking.createdAt ? new Date(selectedBooking.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </div>
                        </div>
                      </div>

                      {/* Financial Breakdown */}
                      <h3 style={{ fontSize: '1rem', fontWeight: '850', color: '#161616', marginBottom: '1rem', borderTop: '1.5px solid #EEF2E6', paddingTop: '1.5rem' }}>Financial Summary</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>
                          <span>Base Court Fee</span>
                          <span>₹{selectedBooking.base_amount?.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>
                          <span>User Platform Fee</span>
                          <span>₹{selectedBooking.user_platform_fee?.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>
                          <span>GST Collected (User)</span>
                          <span>₹{selectedBooking.user_gst?.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: '600', color: '#374151', borderBottom: '1.5px dashed #EEF2E6', paddingBottom: '12px' }}>
                          <span>Partner platform fee + GST</span>
                          <span style={{ color: '#ef4444' }}>-₹{(selectedBooking.partner_platform_fee + selectedBooking.partner_gst)?.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', fontWeight: '800', color: '#084734' }}>
                          <span>Admin Revenue (Platform Fees + GST)</span>
                          <span>₹{selectedBooking.admin_earnings?.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', fontWeight: '800', color: '#10b981' }}>
                          <span>Partner (Owner) Payout</span>
                          <span>₹{selectedBooking.partner_payout?.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', fontWeight: '900', color: '#161616', borderTop: '1.5px solid #EEF2E6', paddingTop: '12px' }}>
                          <span>Total Amount Paid by User</span>
                          <span>₹{selectedBooking.total_price?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT PANEL: User Profile & Turf Owner profiles */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                      {/* Customer Card */}
                      <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.2rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '10px' }}>
                          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#CEF17B', color: '#084734', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '1rem' }}>
                            {selectedBooking.user_name?.charAt(0).toUpperCase() || 'C'}
                          </div>
                          <div>
                            <h4 style={{ margin: 0, fontWeight: '850', color: '#161616', fontSize: '0.95rem' }}>Customer Profile</h4>
                            <span style={{ fontSize: '0.7rem', color: '#98A2B3', fontWeight: '600' }}>Booked By</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div>
                            <span style={{ fontSize: '0.7rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Full Name</span>
                            <div style={{ fontWeight: '700', color: '#161616', marginTop: '2px', fontSize: '0.9rem' }}>{selectedBooking.user_name || '-'}</div>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.7rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Phone Number</span>
                            <div style={{ fontWeight: '700', color: '#161616', marginTop: '2px', fontSize: '0.9rem' }}>{selectedBooking.user_phone || '-'}</div>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.7rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Email Address</span>
                            <div style={{ fontWeight: '700', color: '#161616', marginTop: '2px', fontSize: '0.9rem' }}>{selectedBooking.user_email || 'No email registered'}</div>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.7rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Registered On</span>
                            <div style={{ fontWeight: '700', color: '#161616', marginTop: '2px', fontSize: '0.9rem' }}>
                              {selectedBooking.user_created_at ? new Date(selectedBooking.user_created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Venue & Owner Card */}
                      <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.2rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '10px' }}>
                          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#DCEFB8', color: '#084734', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '1rem' }}>
                            🏟️
                          </div>
                          <div>
                            <h4 style={{ margin: 0, fontWeight: '850', color: '#161616', fontSize: '0.95rem' }}>Venue & Partner</h4>
                            <span style={{ fontSize: '0.7rem', color: '#98A2B3', fontWeight: '600' }}>Host details</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div>
                            <span style={{ fontSize: '0.7rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Venue Name</span>
                            <div style={{ fontWeight: '700', color: '#161616', marginTop: '2px', fontSize: '0.9rem' }}>{selectedBooking.turf_name || '-'}</div>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.7rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Venue Location</span>
                            <div style={{ fontWeight: '700', color: '#161616', marginTop: '2px', fontSize: '0.9rem' }}>
                              {selectedBooking.turf_location ? `${selectedBooking.turf_location}, ${selectedBooking.turf_city || ''}` : '-'}
                            </div>
                          </div>
                          <div style={{ borderTop: '1.5px solid #F1F5F0', paddingTop: '10px', marginTop: '5px' }}>
                            <span style={{ fontSize: '0.72rem', color: '#374151', fontWeight: '800', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>Turf Owner Profile</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div>
                                <span style={{ fontSize: '0.65rem', color: '#98A2B3', fontWeight: '700', textTransform: 'uppercase' }}>Owner Name</span>
                                <div style={{ fontWeight: '700', color: '#084734', fontSize: '0.88rem' }}>{selectedBooking.owner_name || 'No owner name'}</div>
                              </div>
                              <div>
                                <span style={{ fontSize: '0.65rem', color: '#98A2B3', fontWeight: '700', textTransform: 'uppercase' }}>Owner Phone</span>
                                <div style={{ fontWeight: '700', color: '#161616', fontSize: '0.88rem' }}>{selectedBooking.owner_phone || '-'}</div>
                              </div>
                              <div>
                                <span style={{ fontSize: '0.65rem', color: '#98A2B3', fontWeight: '700', textTransform: 'uppercase' }}>Owner Email</span>
                                <div style={{ fontWeight: '700', color: '#161616', fontSize: '0.88rem' }}>{selectedBooking.owner_email || 'No email registered'}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* ── LIST VIEW ── */
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <div style={{ fontWeight: '800', color: '#0f172a' }}>Bookings ({filteredBookings.length})</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {filteredBookings.length > 0 ? filteredBookings.map((b, i) => (
                      <div 
                        key={b._id} 
                        onClick={() => setSelectedBooking(b)}
                        style={{ 
                          background: 'white', padding: '1.5rem', borderRadius: '20px', 
                          border: '1.5px solid #EEF2E6', display: 'flex', 
                          justifyContent: 'space-between', alignItems: 'center', 
                          cursor: 'pointer', transition: '0.2s',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.01)'
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#CEF17B'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#EEF2E6'}
                      >
                        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#F8FAF7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', border: '1px solid #EEF2E6' }}>
                            ⚽
                          </div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontWeight: '900', color: '#084734', fontSize: '0.9rem' }}>
                                #{b.booking_id?.toUpperCase() || b._id?.slice(-8).toUpperCase()}
                              </span>
                              <span style={{ fontSize: '0.72rem', background: '#F8FAF7', color: '#374151', padding: '2px 8px', borderRadius: '6px', fontWeight: '700' }}>
                                {b.sport || 'General'}
                              </span>
                              <span style={{ 
                                fontSize: '0.72rem', 
                                background: b.status === 'confirmed' || b.status === 'completed' || b.status === 'checked-in' ? '#D1FAE5' : b.status === 'cancelled' || b.status === 'refunded' ? '#FEE2E2' : '#FEF3C7',
                                color: b.status === 'confirmed' || b.status === 'completed' || b.status === 'checked-in' ? '#065F46' : b.status === 'cancelled' || b.status === 'refunded' ? '#DC2626' : '#92400E',
                                padding: '2px 8px', borderRadius: '6px', fontWeight: '800', textTransform: 'uppercase'
                              }}>
                                {b.status}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '600', marginTop: '4px' }}>
                              <strong>{b.turf_name}</strong> · Booked by {b.user_name}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '3rem', alignItems: 'center' }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: '850', color: '#161616' }}>
                              {b.date ? new Date(b.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: '#374151', fontWeight: '600', marginTop: '2px' }}>
                              {(b.time_slots || [b.time_slot])[0]} {(b.time_slots || []).length > 1 ? `+${(b.time_slots).length - 1} slots` : ''}
                            </div>
                          </div>

                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '1rem', fontWeight: '900', color: '#084734' }}>₹{(b.total_price || 0).toLocaleString()}</div>
                            <span style={{ fontSize: '0.65rem', color: '#98A2B3', fontWeight: '750', textTransform: 'uppercase' }}>Total Paid</span>
                          </div>

                          <button 
                            onClick={(e) => { e.stopPropagation(); setSelectedBooking(b); }}
                            style={{ 
                              background: '#F8FAF7', border: '1.5px solid #EEF2E6', 
                              padding: '8px 16px', borderRadius: '10px', color: '#084734', 
                              fontWeight: '800', fontSize: '0.78rem', cursor: 'pointer',
                              transition: '0.2s'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#CEF17B'; e.currentTarget.style.background = '#DCEFB8'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#EEF2E6'; e.currentTarget.style.background = '#F8FAF7'; }}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    )) : (
                      <div style={{ background: 'white', padding: '3rem', textAlign: 'center', borderRadius: '20px', border: '1.5px solid #EEF2E6', color: '#374151', fontWeight: '700' }}>
                        No bookings found for the selected date filter
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* -- SUPPORT TICKETS TAB -- */}
          {tab === 'support' && (
            <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', overflow: 'hidden' }}>
              {sideLoading ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>Loading support tickets...</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: '#F8FAF7' }}>
                    <tr>
                      {['User', 'Subject', 'Category', 'Created', 'Status', 'Action'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {supportTickets.length > 0 ? supportTickets.map((t) => (
                      <tr key={t._id} style={{ borderBottom: '1px solid #EEF2E6' }}>
                        <td style={{ ...tdStyle, fontWeight: '800', color: '#0f172a', fontSize: '0.85rem' }}>
                          <div>{t.user_id?.name || ''}</div>
                          <div style={{ fontSize: '0.72rem', color: '#374151' }}>{t.user_id?.phone || ''}</div>
                        </td>
                        <td style={{ ...tdStyle, fontWeight: '700', color: '#374151', fontSize: '0.85rem', maxWidth: '200px' }}>{t.subject}</td>
                        <td style={tdStyle}><span style={numBadge}>{t.category || 'Other'}</span></td>
                        <td style={{ ...tdStyle, fontWeight: '600', color: '#374151', fontSize: '0.8rem' }}>
                          {t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-IN') : ''}
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            background: t.status === 'Resolved' || t.status === 'Closed' ? '#D1FAE5' : t.status === 'In Progress' ? '#DBEAFE' : '#FEF3C7',
                            color: t.status === 'Resolved' || t.status === 'Closed' ? '#065F46' : t.status === 'In Progress' ? '#1D4ED8' : '#92400E',
                            padding: '4px 10px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: '800',
                          }}>{t.status || 'Open'}</span>
                        </td>
                        <td style={tdStyle}>
                          <select
                            defaultValue={t.status || 'Open'}
                            onChange={async (e) => {
                              try {
                                await axios.patch(`${API}/admin/support/tickets/${t._id}`, { status: e.target.value }, { headers: { Authorization: `Bearer ${token}` } });
                                fetchSupport();
                                showAdminToast('Ticket status updated');
                              } catch { showAdminToast('Failed to update ticket', 'error'); }
                            }}
                            style={{ padding: '5px 8px', borderRadius: '7px', border: '1.5px solid #E9EDE8', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer', outline: 'none' }}
                          >
                            <option>Open</option>
                            <option>In Progress</option>
                            <option>Resolved</option>
                            <option>Closed</option>
                          </select>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>No support tickets yet</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* -- OFFERS TAB -- */}
          {tab === 'offers' && (
            <div>
              {selectedOffer ? (
                /* ── DETAILED OFFER VIEW ── */
                <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
                  <button 
                    onClick={() => setSelectedOffer(null)} 
                    style={{ 
                      display: 'flex', alignItems: 'center', gap: '8px', 
                      background: 'none', border: 'none', color: '#084734', 
                      fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer', 
                      marginBottom: '1.5rem', padding: 0 
                    }}
                  >
                    <ArrowLeft size={16} /> Back to Offers
                  </button>

                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '2rem' }}>
                    {/* LEFT PANEL: Offer details */}
                    <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '2rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '1rem' }}>
                        <div>
                          <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Offer ID</span>
                          <h2 style={{ fontSize: '1.5rem', fontWeight: '900', color: '#161616', margin: '4px 0 0 0' }}>
                            #{selectedOffer._id?.slice(-8).toUpperCase()}
                          </h2>
                        </div>
                        <span style={{ 
                          background: selectedOffer.valid_until && new Date(selectedOffer.valid_until) < now ? '#FEE2E2' : '#D1FAE5',
                          color: selectedOffer.valid_until && new Date(selectedOffer.valid_until) < now ? '#DC2626' : '#065F46',
                          padding: '6px 14px', borderRadius: '10px', fontSize: '0.8rem', fontWeight: '900', textTransform: 'uppercase'
                        }}>
                          {selectedOffer.valid_until && new Date(selectedOffer.valid_until) < now ? 'Expired' : 'Active'}
                        </span>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Offer Title</div>
                          <div style={{ fontWeight: '800', color: '#161616', fontSize: '1.1rem' }}>{selectedOffer.title}</div>
                        </div>

                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Discount Value</div>
                          <div style={{ fontWeight: '900', color: '#084734', fontSize: '1.4rem' }}>{selectedOffer.discount}</div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Start Date</div>
                            <div style={{ fontWeight: '700', color: '#161616' }}>
                              {selectedOffer.valid_from ? new Date(selectedOffer.valid_from).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Immediate'}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Expiry Date</div>
                            <div style={{ fontWeight: '700', color: '#161616' }}>
                              {selectedOffer.valid_until ? new Date(selectedOffer.valid_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Never'}
                            </div>
                          </div>
                        </div>

                        {selectedOffer.description && (
                          <div style={{ borderTop: '1.5px solid #EEF2E6', paddingTop: '1.2rem' }}>
                            <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Description</div>
                            <div style={{ fontSize: '0.88rem', color: '#374151', fontWeight: '500', lineHeight: '1.5' }}>{selectedOffer.description}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* RIGHT PANEL: Owner Profile & Venues */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                      {/* Owner Details */}
                      {selectedOffer.owner && (
                        <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '1.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.2rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '10px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#CEF17B', color: '#084734', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '1rem' }}>
                              👤
                            </div>
                            <div>
                              <h4 style={{ margin: 0, fontWeight: '850', color: '#161616', fontSize: '0.95rem' }}>Partner Profile</h4>
                              <span style={{ fontSize: '0.7rem', color: '#98A2B3', fontWeight: '600' }}>Created By</span>
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                              <span style={{ color: '#64748b', fontWeight: '600' }}>Partner Name</span>
                              <span style={{ color: '#161616', fontWeight: '750' }}>{selectedOffer.owner.name}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                              <span style={{ color: '#64748b', fontWeight: '600' }}>Phone Number</span>
                              <span style={{ color: '#161616', fontWeight: '750' }}>{selectedOffer.owner.phone}</span>
                            </div>
                            <button 
                              onClick={() => {
                                const userObj = allUsers.find(u => u._id === selectedOffer.owner.id || u.id === selectedOffer.owner.id);
                                if (userObj) {
                                  setSelectedUser(userObj);
                                  setTab('users');
                                } else {
                                  setTab('users');
                                }
                              }}
                              style={{ width: '100%', background: '#F8FAF7', border: '1.5px solid #EEF2E6', padding: '8px', borderRadius: '10px', fontWeight: '750', fontSize: '0.8rem', color: '#084734', cursor: 'pointer', marginTop: '4px' }}
                            >
                              View Partner Profile
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Applicable Venues */}
                      <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', padding: '1.5rem' }}>
                        <h4 style={{ margin: '0 0 1rem 0', fontWeight: '850', color: '#161616', fontSize: '0.95rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '10px' }}>
                          Applicable Venues ({selectedOffer.turfs?.length || 0})
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto' }}>
                          {selectedOffer.turfs && selectedOffer.turfs.length > 0 ? (
                            selectedOffer.turfs.map(t => (
                              <div 
                                key={t._id || t.id}
                                onClick={() => {
                                  const venueObj = allVenues.find(v => v._id === t._id || v.id === t.id || v._id === t.id);
                                  if (venueObj) {
                                    setSelectedVenue(venueObj);
                                    setTab('venues');
                                  } else {
                                    setTab('venues');
                                  }
                                }}
                                style={{ padding: '10px 12px', background: '#F8FAF7', border: '1.5px solid #EEF2E6', borderRadius: '12px', cursor: 'pointer', transition: 'border 0.2s' }}
                                onMouseEnter={e => e.currentTarget.style.borderColor = '#CEF17B'}
                                onMouseLeave={e => e.currentTarget.style.borderColor = '#EEF2E6'}
                              >
                                <div style={{ fontSize: '0.88rem', fontWeight: '800', color: '#161616' }}>{t.name}</div>
                                <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: '600', marginTop: '2px' }}>📍 {t.location ? `${t.location}, ` : ''}{t.city}</div>
                              </div>
                            ))
                          ) : (
                            <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>All Venues</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* ── OFFERS LIST PAGE ── */
                <div>
                  <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '600' }}>
                      {allOffers.length} offers active/expired in database
                    </span>
                    <button onClick={fetchOffers} style={{ background: '#F3F4F6', border: 'none', padding: '10px 16px', borderRadius: '10px', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', color: '#374151' }}>
                      Refresh List
                    </button>
                  </div>

                  <div style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', overflow: 'hidden' }}>
                    {sideLoading ? (
                      <div style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>Loading offers...</div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#F8FAF7' }}>
                          <tr>
                            {['#', 'Offer Details', 'Discount', 'Owner / Host', 'Venues', 'Validity', 'Status', 'Actions'].map(h => (
                              <th key={h} style={thStyle}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {allOffers.length > 0 ? allOffers.map((o, idx) => {
                            const expired = o.valid_until && new Date(o.valid_until) < now;
                            const status = expired ? 'Expired' : 'Active';
                            
                            return (
                              <tr 
                                key={o._id}
                                onClick={() => setSelectedOffer(o)}
                                style={{ borderBottom: '1px solid #EEF2E6', cursor: 'pointer', transition: 'background 0.2s' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#F8FAF7'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              >
                                <td style={{ ...tdStyle, color: '#374151', fontWeight: '700' }}>{idx + 1}</td>
                                <td style={tdStyle}>
                                  <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.88rem' }}>{o.title}</div>
                                  <div style={{ fontSize: '0.72rem', color: '#98A2B3', marginTop: '2px' }}>ID: {o._id?.slice(-8).toUpperCase()}</div>
                                </td>
                                <td style={tdStyle}>
                                  <span style={{ fontWeight: '800', color: '#084734', background: '#DCEFB8', padding: '3px 8px', borderRadius: '6px', fontSize: '0.82rem' }}>
                                    {o.discount}
                                  </span>
                                </td>
                                <td style={tdStyle}>
                                  <div style={{ fontSize: '0.83rem', color: '#374151', fontWeight: '700' }}>{o.owner?.name || '—'}</div>
                                  <div style={{ fontSize: '0.72rem', color: '#98A2B3', fontWeight: '600' }}>{o.owner?.phone || '—'}</div>
                                </td>
                                <td style={tdStyle}>
                                  <span style={{ fontSize: '0.82rem', fontWeight: '600', color: '#374151' }}>
                                    {o.turfs && o.turfs.length > 0 
                                      ? `${o.turfs.length} venue(s)` 
                                      : (o.turf_id?.name ? '1 venue' : 'All venues')}
                                  </span>
                                </td>
                                <td style={tdStyle}>
                                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#4b5563' }}>
                                    {o.valid_from ? new Date(o.valid_from).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''} 
                                    {o.valid_from && o.valid_until ? ' to ' : 'Expires: '}
                                    {o.valid_until ? new Date(o.valid_until).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : 'Ongoing'}
                                  </div>
                                </td>
                                <td style={tdStyle}>
                                  <span style={{
                                    background: status === 'Active' ? '#D1FAE5' : '#FEE2E2',
                                    color: status === 'Active' ? '#065F46' : '#DC2626',
                                    padding: '3px 10px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: '800',
                                  }}>{status}</span>
                                </td>
                                <td style={tdStyle}>
                                  <button 
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!window.confirm(`Permanently delete offer "${o.title}"?`)) return;
                                      try {
                                        await axios.delete(`${API}/offers/${o._id}`, { headers: { Authorization: `Bearer ${token}` } });
                                        fetchOffers();
                                        showAdminToast('Offer deleted successfully');
                                      } catch {
                                        showAdminToast('Failed to delete offer', 'error');
                                      }
                                    }}
                                    style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', padding: '5px 10px', borderRadius: '8px', fontWeight: '700', fontSize: '0.75rem', cursor: 'pointer' }}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            );
                          }) : (
                            <tr><td colSpan="8" style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>No offers in database</td></tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'tournaments' && (
            <AdminTournaments token={token} onToast={showAdminToast} />
          )}

          {tab === 'kyc' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {/* Stat Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem' }}>
                <StatCard
                  label="TOTAL KYC SUBMISSIONS"
                  value={kycList.length}
                  sub="All partner applications"
                  icon={<FileText size={20} />}
                  color="#084734"
                />
                <StatCard
                  label="PENDING VERIFICATION"
                  value={kycList.filter(k => k.status === 'pending').length}
                  sub="Requires admin review"
                  icon={<Clock size={20} />}
                  color="#D97706"
                />
                <StatCard
                  label="APPROVED PARTNERS"
                  value={kycList.filter(k => k.status === 'approved').length}
                  sub="Verified & active credentials"
                  icon={<ShieldCheck size={20} />}
                  color="#10B981"
                />
                <StatCard
                  label="REJECTED / ACTION REQ."
                  value={kycList.filter(k => k.status === 'rejected').length}
                  sub="Returned for correction"
                  icon={<X size={20} />}
                  color="#DC2626"
                />
              </div>

              {/* Table Card with Filter & Search */}
              <div style={{ background: 'white', borderRadius: '24px', border: '1.5px solid #EEF2E6', overflow: 'hidden' }}>
                <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderBottom: '1.5px solid #EEF2E6' }}>
                  <div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '900', color: '#0f172a', margin: 0 }}>Partner KYC Verification Records</h3>
                    <p style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: '600', margin: '4px 0 0 0' }}>
                      Review identity documents (Aadhaar, Passport, Driving License, Voter ID) and approve or reject partner registrations.
                    </p>
                  </div>

                  {/* Filter & Search Bar */}
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        placeholder="Search name, phone, doc no..."
                        value={kycSearch}
                        onChange={e => setKycSearch(e.target.value)}
                        style={{
                          padding: '8px 14px 8px 34px', borderRadius: '10px', border: '1.5px solid #EEF2E6',
                          fontSize: '0.85rem', outline: 'none', background: '#F8FAF7', fontWeight: '600', width: '220px'
                        }}
                      />
                      <Search size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    </div>

                    <div style={{ display: 'flex', background: '#F8FAF7', padding: '4px', borderRadius: '10px', border: '1.5px solid #EEF2E6' }}>
                      {['all', 'pending', 'approved', 'rejected'].map(status => (
                        <button
                          key={status}
                          onClick={() => setKycStatusFilter(status)}
                          style={{
                            background: kycStatusFilter === status ? '#084734' : 'transparent',
                            color: kycStatusFilter === status ? '#CEF17B' : '#475569',
                            border: 'none', padding: '6px 14px', borderRadius: '8px',
                            fontWeight: '800', fontSize: '0.75rem', cursor: 'pointer', textTransform: 'capitalize',
                            transition: 'all 0.15s'
                          }}
                        >
                          {status} {status !== 'all' ? `(${kycList.filter(k => k.status === status).length})` : ''}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={fetchKYC}
                      style={{
                        background: '#F8FAF7', border: '1.5px solid #EEF2E6', borderRadius: '10px',
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                        fontWeight: '700', fontSize: '0.8rem', color: '#084734'
                      }}
                    >
                      <RefreshCw size={14} /> Refresh
                    </button>
                  </div>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                  {sideLoading ? (
                    <div style={{ padding: '4rem', textAlign: 'center', color: '#64748b', fontWeight: '700' }}>
                      Loading KYC records...
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ background: '#F8FAF7', borderBottom: '1.5px solid #EEF2E6' }}>
                        <tr>
                          <th style={thStyle}>#</th>
                          <th style={thStyle}>Partner / Business</th>
                          <th style={thStyle}>Contact Details</th>
                          <th style={thStyle}>Document Type</th>
                          <th style={thStyle}>Document Number</th>
                          <th style={thStyle}>Uploaded Document Photo</th>
                          <th style={thStyle}>Submitted Date</th>
                          <th style={thStyle}>Status</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const filteredKyc = (kycList || []).filter(k => {
                            const matchesStatus = kycStatusFilter === 'all' || k.status === kycStatusFilter;
                            const q = kycSearch.toLowerCase().trim();
                            const matchesSearch = !q || (
                              (k.user_name && k.user_name.toLowerCase().includes(q)) ||
                              (k.business_name && k.business_name.toLowerCase().includes(q)) ||
                              (k.user_phone && k.user_phone.toLowerCase().includes(q)) ||
                              (k.user_email && k.user_email.toLowerCase().includes(q)) ||
                              (k.document_type && k.document_type.toLowerCase().includes(q)) ||
                              (k.document_number && k.document_number.toLowerCase().includes(q))
                            );
                            return matchesStatus && matchesSearch;
                          });

                          if (filteredKyc.length === 0) {
                            return (
                              <tr>
                                <td colSpan="9" style={{ padding: '4rem', textAlign: 'center', color: '#64748b', fontWeight: '600' }}>
                                  No KYC requests found matching your filters.
                                </td>
                              </tr>
                            );
                          }

                          return filteredKyc.map((k, idx) => {
                            const docImgUrl = getDocUrl(k.document_image);
                            return (
                              <tr
                                key={k.id || k._id || idx}
                                style={{ borderBottom: '1px solid #EEF2E6', transition: 'background 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#F8FAF7'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              >
                                <td style={{ ...tdStyle, fontWeight: '700', color: '#64748b' }}>{idx + 1}</td>
                                <td style={tdStyle}>
                                  <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.9rem' }}>
                                    {k.user_name || k.business_name || 'Partner'}
                                  </div>
                                  {k.business_name && k.business_name !== k.user_name && (
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>
                                      🏢 {k.business_name}
                                    </div>
                                  )}
                                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                                    Partner ID: {(k.user_id?._id || k.user_id || k.id || '').toString().slice(-8).toUpperCase()}
                                  </div>
                                </td>
                                <td style={tdStyle}>
                                  <div style={{ fontSize: '0.83rem', color: '#0f172a', fontWeight: '700' }}>{k.user_phone || '—'}</div>
                                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{k.user_email || '—'}</div>
                                </td>
                                <td style={tdStyle}>
                                  <span style={{
                                    fontWeight: '800', color: '#084734', background: '#DCEFB8',
                                    padding: '4px 10px', borderRadius: '8px', fontSize: '0.8rem', display: 'inline-block'
                                  }}>
                                    {k.document_type || '—'}
                                  </span>
                                </td>
                                <td style={tdStyle}>
                                  <span style={{ fontFamily: 'monospace', fontWeight: '800', color: '#0f172a', fontSize: '0.88rem' }}>
                                    {k.document_number || '—'}
                                  </span>
                                </td>
                                <td style={tdStyle}>
                                  {docImgUrl ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <img
                                        src={docImgUrl}
                                        alt="KYC Document"
                                        onClick={() => setPreviewKycImage({
                                          url: docImgUrl,
                                          title: `${k.user_name || k.business_name || 'Partner'} - ${k.document_type || 'Document'}`,
                                          number: k.document_number
                                        })}
                                        style={{
                                          width: '46px', height: '46px', objectFit: 'cover', borderRadius: '8px',
                                          border: '1.5px solid #DCEFB8', cursor: 'pointer', transition: 'transform 0.15s',
                                          boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
                                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                        title="Click to view full photo"
                                      />
                                      <button
                                        onClick={() => setPreviewKycImage({
                                          url: docImgUrl,
                                          title: `${k.user_name || k.business_name || 'Partner'} - ${k.document_type || 'Document'}`,
                                          number: k.document_number
                                        })}
                                        style={{
                                          background: '#F0FDF4', color: '#084734', border: '1px solid #BBF7D0',
                                          padding: '4px 10px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '800',
                                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                                        }}
                                      >
                                        <Eye size={13} /> View Photo
                                      </button>
                                    </div>
                                  ) : (
                                    <span style={{ color: '#94a3b8', fontSize: '0.78rem', fontStyle: 'italic' }}>
                                      No photo uploaded
                                    </span>
                                  )}
                                </td>
                                <td style={tdStyle}>
                                  <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151' }}>
                                    {k.created_at ? new Date(k.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                  </div>
                                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                    {k.created_at ? new Date(k.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                                  </div>
                                </td>
                                <td style={tdStyle}>
                                  <span style={{
                                    background: k.status === 'approved' ? '#D1FAE5' : k.status === 'rejected' ? '#FEE2E2' : '#FEF3C7',
                                    color: k.status === 'approved' ? '#065F46' : k.status === 'rejected' ? '#DC2626' : '#92400E',
                                    padding: '4px 10px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: '800', textTransform: 'uppercase'
                                  }}>
                                    {k.status}
                                  </span>
                                  {k.admin_note && (
                                    <div style={{ fontSize: '0.7rem', color: '#DC2626', marginTop: '4px', maxWidth: '140px' }} title={k.admin_note}>
                                      Note: {k.admin_note}
                                    </div>
                                  )}
                                </td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>
                                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                    {k.status !== 'approved' && (
                                      <button
                                        onClick={() => handleReviewKYC(k.id || k._id, 'approved')}
                                        style={{
                                          background: '#D1FAE5', color: '#065F46', border: 'none',
                                          padding: '6px 14px', borderRadius: '8px', fontWeight: '800', fontSize: '0.75rem',
                                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                                          transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                      >
                                        <Check size={14} /> Approve
                                      </button>
                                    )}
                                    {k.status !== 'rejected' && (
                                      <button
                                        onClick={() => {
                                          setReviewKycTarget(k);
                                          setReviewKycStatus('rejected');
                                          setReviewKycNote('');
                                          setReviewKycModal(true);
                                        }}
                                        style={{
                                          background: '#FEE2E2', color: '#DC2626', border: 'none',
                                          padding: '6px 14px', borderRadius: '8px', fontWeight: '800', fontSize: '0.75rem',
                                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                                          transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                      >
                                        <X size={14} /> Reject
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === 'fee_settings' && (
            <AdminFeeSettings token={token} onToast={showAdminToast} />
          )}

        </div>
      </div>

      {/* KYC DOCUMENT PHOTO PREVIEW MODAL */}
      {previewKycImage && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
          zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1.5rem', WebkitBackdropFilter: 'blur(4px)', backdropFilter: 'blur(4px)',
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'white', borderRadius: '24px', maxWidth: '800px', width: '100%',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 25px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1.25rem 1.5rem', background: '#084734', color: 'white',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#CEF17B' }}>
                  {previewKycImage.title || 'Partner KYC Document Photo'}
                </h3>
                {previewKycImage.number && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', opacity: 0.85, fontFamily: 'monospace' }}>
                    Document Number: {previewKycImage.number}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <a
                  href={previewKycImage.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none',
                    padding: '8px 12px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '700',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none'
                  }}
                >
                  <ExternalLink size={14} /> Open Full Size
                </a>
                <button
                  onClick={() => setPreviewKycImage(null)}
                  style={{
                    background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none',
                    borderRadius: '8px', width: '34px', height: '34px', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Modal Body / Image */}
            <div style={{
              padding: '1.5rem', background: '#0f172a', display: 'flex',
              alignItems: 'center', justifyContent: 'center', overflow: 'auto', flex: 1, minHeight: '300px'
            }}>
              <img
                src={previewKycImage.url}
                alt="Partner KYC Document"
                style={{
                  maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain',
                  borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.4)'
                }}
              />
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '1rem 1.5rem', background: '#F8FAF7', borderTop: '1.5px solid #EEF2E6', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setPreviewKycImage(null)}
                style={{
                  background: '#084734', color: '#CEF17B', border: 'none',
                  padding: '10px 20px', borderRadius: '10px', fontWeight: '800',
                  fontSize: '0.85rem', cursor: 'pointer'
                }}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REJECT KYC NOTE MODAL */}
      {reviewKycModal && reviewKycTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1.5rem', WebkitBackdropFilter: 'blur(3px)', backdropFilter: 'blur(3px)',
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'white', borderRadius: '24px', maxWidth: '480px', width: '100%',
            overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.25)'
          }}>
            <div style={{ padding: '1.25rem 1.5rem', background: '#FEE2E2', borderBottom: '1.5px solid #FECACA', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '900', color: '#991B1B' }}>
                Reject KYC Application
              </h3>
              <button
                onClick={() => setReviewKycModal(false)}
                style={{ background: 'transparent', border: 'none', color: '#991B1B', cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '1.5rem' }}>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#374151', fontWeight: '600' }}>
                Please provide a rejection note for <strong>{reviewKycTarget.user_name || reviewKycTarget.business_name || 'the partner'}</strong>. This will help them understand why the verification failed and re-upload the correct document.
              </p>

              <textarea
                placeholder="e.g. Document photo is blurry, Document number does not match image, Expired ID proof, etc."
                value={reviewKycNote}
                onChange={e => setReviewKycNote(e.target.value)}
                rows={4}
                style={{
                  width: '100%', padding: '12px', borderRadius: '12px',
                  border: '1.5px solid #EEF2E6', fontSize: '0.85rem', fontWeight: '600',
                  outline: 'none', background: '#F8FAF7', resize: 'vertical', boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ padding: '1rem 1.5rem', background: '#F8FAF7', borderTop: '1.5px solid #EEF2E6', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setReviewKycModal(false)}
                disabled={reviewKycSaving}
                style={{
                  background: 'white', color: '#374151', border: '1.5px solid #EEF2E6',
                  padding: '10px 18px', borderRadius: '10px', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleReviewKYC(reviewKycTarget.id || reviewKycTarget._id, 'rejected', reviewKycNote)}
                disabled={reviewKycSaving}
                style={{
                  background: '#DC2626', color: 'white', border: 'none',
                  padding: '10px 20px', borderRadius: '10px', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer'
                }}
              >
                {reviewKycSaving ? 'Saving...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  );
}

function StatCard({ label, value, sub, icon, color }) {
  return (
    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '20px', border: '1.5px solid #EEF2E6', animation: 'fadeIn 0.4s ease-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151' }}>{label}</div>
        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: `${color}15`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </div>
      </div>
      <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#0f172a', marginBottom: '6px' }}>{value}</div>
      <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#374151' }}>{sub}</div>
    </div>
  );
}

function FeeBox({ label, amount, desc, color, highlight }) {
  return (
    <div style={{ padding: '1.5rem', borderRadius: '16px', background: highlight ? '#DCEFB8' : '#F8FAF7', border: `1.5px solid ${highlight ? '#DCEFB8' : '#EEF2E6'}` }}>
      <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: '2rem', fontWeight: '900', color, marginBottom: '6px' }}>{amount}</div>
      <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#374151' }}>{desc}</div>
    </div>
  );
}

const thStyle = {
  textAlign: 'left', padding: '1rem 1.2rem', fontSize: '0.72rem',
  fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px'
};
const tdStyle = { padding: '1rem 1.2rem', verticalAlign: 'middle' };
const numBadge = {
  background: '#EEF2E6', color: '#374151', padding: '3px 10px',
  borderRadius: '8px', fontWeight: '800', fontSize: '0.85rem'
};
const btnPrimary = {
  background: '#CEF17B', color: '#084734', border: 'none', padding: '10px 18px',
  borderRadius: '10px', fontWeight: '800', fontSize: '0.85rem',
  display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'
};
const btnSecondary = {
  background: 'white', color: '#374151', border: '1.5px solid #DCEFB8', padding: '10px 18px',
  borderRadius: '10px', fontWeight: '700', fontSize: '0.85rem',
  display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'
};
